import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CustomCredentialFlow } from '../components/CustomCredentialFlow';
import { LayoutGrid, Plug, MessageSquare, UserCircle, Map, Activity, LayoutTemplate, FolderOpen, Monitor, ChevronDown, Bookmark, Plus, Settings, Paperclip, Send, X, Search, Clock, FileText, Folder, Play, Pause, CheckCircle2, Sparkles, ArrowRight, ChevronLeft, ChevronRight, ListChecks, Globe, Loader2, Lock, Users, Zap, Star, MoreHorizontal, Info, Pencil, Trash2, Download, Sun, Moon, History, AlertTriangle, Filter, Check, Bell, Minus, ChevronUp, ChevronsUpDown, PhoneCall, GraduationCap, Layers, CreditCard, LogOut } from 'lucide-react';
import { BillingModal } from '../components/BillingModal';
import { TracksView } from '../components/dashboard/TracksView';
import { GroupsView } from '../components/dashboard/GroupsView';
import { TablesView } from '../components/dashboard/TablesView';
import { TRACKS } from '../data/workspaceTracks';
// Recent chats shown in the sidebar. Kept short on purpose — it's a "pick up
// where you left off" list, not an archive (that's the Chats view).
const SIDEBAR_CHATS = ['SideShift connection check', 'Company Brain campaign brief', 'Q3 pipeline review', 'Creator outreach draft'];
// ── Activity log types ───────────────────────────────────────────────────────
// A system-wide, read-only feed of everything Giga did on your behalf. The
// source of one entry is the kind of automation that triggered it; the status
// is the outcome of that run. Both drive the Activity view's filters.
type ActivityType = 'Chat' | 'Pulse' | 'Journey' | 'Recipe' | 'Assistant';
type ActivityStatus = 'success' | 'running' | 'needs-attention';
// Timing filter ranges.
type ActivityRange = 'all' | '7d' | '30d' | '90d' | '6m' | '12m' | 'custom';
type ActivityEntry = {
  task: string;
  detail: string;
  type: ActivityType;
  status: ActivityStatus;
  source: string;
  tool: string;
  // Days ago used for date grouping and the timing filter.
  daysAgo: number;
  time: string;
};
// ── Starter prompt categories & data ────────────────────────────────────────
type StarterCategory = 'comms' | 'operations' | 'admin' | 'growth' | 'insights';
type PlanSite = {
  slug: string;
  label: string;
};
type StarterPrompt = {
  title: string;
  desc: string;
  tools: string[];
  // The domains/tools the plan will act on, shown under "Allow actions on…".
  sites: PlanSite[];
  // The step-by-step approach Giga proposes before acting.
  steps: string[];
};
const STARTER_CATEGORIES: {
  id: StarterCategory;
  label: string;
}[] = [{
  id: 'comms',
  label: 'Comms'
}, {
  id: 'operations',
  label: 'Operations'
}, {
  id: 'admin',
  label: 'Admin'
}, {
  id: 'growth',
  label: 'Growth'
}, {
  id: 'insights',
  label: 'Insights'
}];
const STARTER_PROMPTS: Record<StarterCategory, StarterPrompt[]> = {
  comms: [{
    title: 'Meeting recaps',
    desc: 'When a meeting transcript lands, create tasks for action items and email a recap to attendees.',
    tools: ['gmail', 'asana', 'slack'],
    sites: [{
      slug: 'asana',
      label: 'Asana'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Pull the latest meeting transcript and identify the action items', 'Create an Asana task for each action item with its owner and due date', 'Draft a concise recap summarizing decisions and next steps', 'Email the recap to every attendee from Gmail', 'Post a short summary to the relevant Slack channel']
  }, {
    title: 'Slack reply nudges',
    desc: "When I'm mentioned in a thread but don't reply within 2 hours, text me a reminder with context.",
    tools: ['slack'],
    sites: [{
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch every Slack thread where you are @-mentioned', 'Start a 2-hour timer the moment a mention lands', 'Check whether you have replied before the timer runs out', 'If not, summarize who asked and what they need', 'Send you a reminder with a direct link to the thread']
  }, {
    title: 'Daily briefing',
    desc: 'Send me a morning briefing pulled from my Calendar, Gmail, and open Asana tasks.',
    tools: ['google', 'gmail', 'asana'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ["Pull today's events from Google Calendar", 'Scan Gmail for unread or flagged messages that need a reply', 'List Asana tasks due today or overdue', 'Compose a single, scannable morning briefing', 'Deliver it to you at 8:00am each weekday']
  }],
  operations: [{
    title: 'Protect focus blocks',
    desc: 'On a fragmented day, see if meetings can shift to open up focus blocks.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Calendar'
    }],
    steps: ["Review today's calendar for fragmentation and gaps", 'Identify meetings that could be moved without conflicts', 'Find at least one 90-minute focus block to protect', 'Draft reschedule proposals for the movable meetings', 'Ask for your approval before sending any invites']
  }, {
    title: 'Bug triage pipeline',
    desc: 'When a Sentry error spikes, open a GitHub issue and post the details to Slack.',
    tools: ['sentry', 'github', 'slack'],
    sites: [{
      slug: 'sentry',
      label: 'Sentry'
    }, {
      slug: 'github',
      label: 'GitHub'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Detect when a Sentry error crosses its spike threshold', 'Gather the stack trace, affected users, and release info', 'Open a GitHub issue with the details and a triage label', 'Post an alert with the issue link to the #engineering channel', 'Tag the most likely owner based on recent commits']
  }, {
    title: 'Stale issue sweep',
    desc: 'Each week, find Linear issues with no activity in 14 days and ping their owners.',
    tools: ['linear', 'slack'],
    sites: [{
      slug: 'linear',
      label: 'Linear'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Query Linear for issues with no activity in the last 14 days', 'Group the stale issues by assignee', 'Draft a friendly nudge for each owner with their issue list', 'Send each nudge as a Slack DM', 'Post a summary of the sweep to the team channel']
  }],
  admin: [{
    title: 'Friday inbox cleanup',
    desc: 'Every Friday, archive non-actionable emails and flag anything that needs a reply.',
    tools: ['gmail'],
    sites: [{
      slug: 'gmail',
      label: 'Gmail'
    }],
    steps: ["Scan this week's Gmail inbox and classify each message", 'Archive newsletters and notifications that need no action', 'Flag messages that still need a reply from you', 'Draft quick replies for the most routine threads', 'Send you a short summary of what was cleaned up']
  }, {
    title: 'Invoice reconciliation',
    desc: 'When a payment lands in Stripe, match it to an invoice and update the books.',
    tools: ['stripe', 'notion'],
    sites: [{
      slug: 'stripe',
      label: 'Stripe'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Detect the new payment as it lands in Stripe', 'Match the amount and customer to an open invoice', 'Mark the matched invoice as paid', 'Update the bookkeeping ledger in Notion', 'Flag any payment that has no matching invoice']
  }, {
    title: 'Document filing',
    desc: 'Sort new files into the right Drive folders and rename them to our convention.',
    tools: ['google'],
    sites: [{
      slug: 'google',
      label: 'Google Drive'
    }],
    steps: ['Watch the Drive inbox folder for newly added files', 'Read each file to infer its type and project', 'Rename it to match the team naming convention', 'Move it into the correct project folder', 'Log anything ambiguous for you to confirm']
  }],
  growth: [{
    title: 'Ad spend audit',
    desc: 'Pause ad sets under 0.8x ROAS in Meta Ads and export a summary PDF.',
    tools: ['meta', 'notion'],
    sites: [{
      slug: 'meta',
      label: 'Meta Ads'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ['Pull the last 14 days of spend and ROAS from Meta Ads', 'Flag every ad set performing under 0.8× ROAS', 'Pause the flagged ad sets to stop the bleed', 'Export a summary PDF of what changed and why', 'Save the report to Notion and notify you']
  }, {
    title: 'New deal onboarding',
    desc: 'When a deal closes, create the client workspace and send the welcome sequence.',
    tools: ['notion', 'gmail', 'asana'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'gmail',
      label: 'Gmail'
    }, {
      slug: 'asana',
      label: 'Asana'
    }],
    steps: ['Detect the closed-won deal and pull the client details', 'Spin up the client onboarding workspace in Notion', 'Create the standard onboarding tasks in Asana', 'Send the welcome email sequence from Gmail', 'Notify the account owner that onboarding has kicked off']
  }, {
    title: 'Lead enrichment',
    desc: 'Enrich new CRM contacts with firmographics and route hot ones to sales.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Watch Attio for newly added contacts', 'Enrich each one with firmographic and role data', 'Score the lead against your ideal-customer profile', 'Update the CRM record with the enriched fields', 'Route hot leads to sales in the #pipeline channel']
  }],
  insights: [{
    title: 'Weekly report generation',
    desc: 'Every Monday, pull last week metrics and post a digest to #leadership.',
    tools: ['notion', 'slack'],
    sites: [{
      slug: 'notion',
      label: 'Notion'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ["Pull last week's metrics from the Notion dashboards", 'Compare them against the prior week and targets', 'Highlight the biggest wins and the risks to watch', 'Compose a concise leadership digest', 'Post it to the #leadership channel every Monday at 9am']
  }, {
    title: 'Pipeline cleanup',
    desc: 'Flag stale or at-risk deals and summarize what changed since last week.',
    tools: ['attio', 'slack'],
    sites: [{
      slug: 'attio',
      label: 'Attio'
    }, {
      slug: 'slack',
      label: 'Slack'
    }],
    steps: ['Scan the Attio pipeline for deals with no recent activity', 'Flag deals slipping past their expected close date', 'Summarize what changed on each deal since last week', 'Suggest a next step for every at-risk deal', 'Post the cleanup summary to the sales channel']
  }, {
    title: 'Support trends',
    desc: 'Cluster this week support tickets by theme and surface the top issues.',
    tools: ['crisp', 'notion'],
    sites: [{
      slug: 'crisp',
      label: 'Crisp'
    }, {
      slug: 'notion',
      label: 'Notion'
    }],
    steps: ["Pull this week's resolved and open tickets from Crisp", 'Cluster the tickets by underlying theme', 'Rank the themes by volume and customer impact', 'Write up the top three issues with example tickets', 'Save the trends report to Notion for the team']
  }]
};
// Small tool/site icon chip reused across the rail.
function ToolIcon({
  slug,
  size = 'sm'
}: {
  slug: string;
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'w-6 h-6' : 'w-5 h-5';
  const img = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return <span className={`${box} rounded-md bg-white border border-neutral-200 flex items-center justify-center shrink-0`} data-id="element-8243" data-mp="3p99sthpun1iw">
      <img src={`https://cdn.simpleicons.org/${slug}/475569`} className={`${img} object-contain`} alt="" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
    }} data-id="element-8244" data-mp="3kdkz3v0igeui" />
    </span>;
}
// Claude-style plan flow: shows the picked task as a message, "thinks" for a
// beat, then reveals a numbered plan with the sites it will touch and
// Approve / Make changes actions.
function PlanView({
  prompt,
  onBack
}: {
  prompt: StarterPrompt;
  onBack: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState<'thinking' | 'plan'>(reducedMotion ? 'plan' : 'thinking');
  const [approved, setApproved] = useState(false);
  useEffect(() => {
    if (reducedMotion) {
      setPhase('plan');
      return;
    }
    setPhase('thinking');
    const id = setTimeout(() => setPhase('plan'), 1100);
    return () => clearTimeout(id);
  }, [prompt, reducedMotion]);
  return <div className="flex flex-col" data-id="element-8245" data-mp="3gssony19ukbs">
      {/* Back affordance */}
      <button onClick={onBack} className="self-start flex items-center gap-1 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors mb-4" data-id="element-8246" data-mp="3lsnd7uxkqxto">
        <ChevronLeft size={14} data-id="element-8247" data-mp="25grta09wjxn2" />
        Back to suggestions
      </button>

      {/* User message bubble */}
      <div className="flex justify-end mb-5" data-id="element-8248" data-mp="2abdujam9s9tf">
        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#2B211A] text-white text-sm leading-relaxed px-3.5 py-2.5" data-id="element-8249" data-mp="sfc29d8nm10v">
          {prompt.desc}
        </div>
      </div>

      {/* Working / creating plan */}
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4" data-id="element-8250" data-mp="11fig5089l30k">
        {phase === 'thinking' ? <>
            <Loader2 size={15} className="text-[#EA580C] animate-spin" data-id="element-8251" data-mp="xfzsvxsz0fuk" />
            Creating plan…
          </> : <>
            <Sparkles size={15} className="text-[#EA580C]" data-id="element-8252" data-mp="9n9jnwcgcanz" />
            Here&rsquo;s the plan
          </>}
      </div>

      <AnimatePresence data-id="element-8253" data-mp="ihpbnthzsl12">
        {phase === 'plan' && <motion.div initial={reducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: reducedMotion ? 0 : 0.4,
        ease: 'easeOut'
      }} className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-id="element-8254" data-mp="iiqwgpot8lvz">
            {/* Plan header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100" data-id="element-8255" data-mp="3uvpx51fwhaz8">
              <ListChecks size={16} className="text-neutral-500" data-id="element-8256" data-mp="3pf0dhhxawdp" />
              <span className="text-sm font-semibold text-neutral-900" data-id="element-8257" data-mp="8nq9fq0e92uv">
                Giga&rsquo;s plan
              </span>
            </div>

            {/* Sites it will act on */}
            <div className="px-4 py-3 border-b border-neutral-100" data-id="element-8258" data-mp="dfzxt1lv1uv1">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2.5" data-id="element-8259" data-mp="icq818azhw8j">
                <Globe size={12} data-id="element-8260" data-mp="rdy89vx29759" />
                Allow actions on these
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-8261" data-mp="rc50gdapogvo">
                {prompt.sites.map(site => <span key={site.slug} className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-700" data-id="element-8262" data-mp="wc9elwo5cziq">
                    <ToolIcon slug={site.slug} data-id="element-8263" data-mp="2y4ny306cac19" />
                    {site.label}
                  </span>)}
              </div>
            </div>

            {/* Numbered approach */}
            <div className="px-4 py-3.5" data-id="element-8264" data-mp="2ltehqujcik64">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-3" data-id="element-8265" data-mp="2lsbtkudeqvtw">
                Approach to follow
              </div>
              <ol className="flex flex-col gap-3" data-id="element-8266" data-mp="2bxbipxjsj1ne">
                {prompt.steps.map((step, i) => <motion.li key={i} initial={reducedMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: -6
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: reducedMotion ? 0 : 0.3,
              delay: reducedMotion ? 0 : 0.15 + i * 0.08
            }} className="flex items-start gap-3" data-id="element-8267" data-mp="39ipkghk9aomw" data-mpi={i}>
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full border border-neutral-300 flex items-center justify-center text-[11px] font-semibold text-neutral-500" data-id="element-8268" data-mp="1o77blg7e8jrn">
                      {i + 1}
                    </span>
                    <span className="text-sm text-neutral-700 leading-snug" data-id="element-8269" data-mp="b61fmv61rki0">
                      {step}
                    </span>
                  </motion.li>)}
              </ol>
            </div>

            {/* Actions */}
            <div className="px-4 pb-4 pt-1" data-id="element-8270" data-mp="68j7h6zut9b2">
              {approved ? <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-[#0F9D58]/10 text-[#0F9D58] text-sm font-semibold" data-id="element-8271" data-mp="66yc5f5ptmc5">
                  <CheckCircle2 size={16} data-id="element-8272" data-mp="3sfr5tpccwfxl" />
                  Plan approved — Giga is on it
                </div> : <div className="flex flex-col gap-2" data-id="element-8273" data-mp="3eoupawvpfp6w">
                  <button onClick={() => setApproved(true)} className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-8274" data-mp="3eq5r4r5lamvd">
                    Approve plan
                  </button>
                  <button onClick={onBack} className="w-full flex items-center justify-center gap-2 border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-sm font-semibold py-2.5 rounded-lg transition-colors" data-id="element-8275" data-mp="39o6oxkll5w00">
                    Make changes
                  </button>
                </div>}
              <p className="text-[11px] text-neutral-400 leading-relaxed mt-3" data-id="element-8276" data-mp="302oxzk1awm1c">
                Giga will only use the tools listed. You&rsquo;ll be asked
                before anything else.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ── Chat context controls: Space + Journey pickers ──────────────────────────
// The "+ Space" and "+ Journey" buttons above a chat open lightweight pickers:
// Space is a compact menu of workspaces with a check on the active one;
// Journey is a searchable popover listing the journeys you can attach. Both
// close on outside-click or Escape.
const CHAT_SPACES = ['Company-wide', 'Revenue', 'Marketing', 'Product & Engineering', 'Customer Success', 'Operations', 'Leadership'];
const CHAT_JOURNEYS = ['Trial onboarding & activation', 'Paid acquisition performance', 'Q4 product launch', 'Product roadmap & priorities', 'Customer advocacy program', 'Reliability & incident response'];
function ChatContextControls() {
  // Which picker is open, if any.
  const [open, setOpen] = useState<null | 'space' | 'journey'>(null);
  const [activeSpace, setActiveSpace] = useState('Everyone');
  const [journeyQuery, setJourneyQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Close on outside-click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // Focus the journey search when that popover opens.
  useEffect(() => {
    if (open === 'journey') {
      const id = window.setTimeout(() => searchRef.current?.focus(), 20);
      return () => window.clearTimeout(id);
    }
  }, [open]);
  const filteredJourneys = CHAT_JOURNEYS.filter(j => j.toLowerCase().includes(journeyQuery.trim().toLowerCase()));
  const triggerClass = (isOpen: boolean) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isOpen ? 'bg-neutral-200 text-neutral-900' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'}`;
  return <div ref={wrapRef} className="relative flex items-center gap-2" data-id="element-8277" data-mp="2sitltp9gy91k">
      {/* Space picker */}
      <div className="relative" data-id="element-8278" data-mp="2nhnsbp7zihqz">
        <button onClick={() => setOpen(open === 'space' ? null : 'space')} aria-haspopup="menu" aria-expanded={open === 'space'} className={triggerClass(open === 'space')} data-id="element-8279" data-mp="2nk06dramg7yr">
          <Plus size={14} data-id="element-8280" data-mp="2h9yaw4w3hze2" />
          Space
        </button>
        <AnimatePresence data-id="element-8281" data-mp="2cbd9411737ni">
          {open === 'space' && <motion.div role="menu" initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-72 origin-top-left rounded-xl bg-[#4B463F] text-white shadow-2xl ring-1 ring-black/20 p-1.5" data-id="element-8282" data-mp="2c9k0whaxofde">
              <button role="menuitem" onClick={() => setOpen(null)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition-colors" data-id="element-8283" data-mp="1ojzgdtz4ta20">
                <Plus size={15} className="text-white/70" data-id="element-8284" data-mp="1fngswx9o6ect" />
                New space
              </button>
              <div className="my-1 h-px bg-white/10" data-id="element-8285" data-mp="1aoncox30098y" />
              {CHAT_SPACES.map(s => {
            const active = activeSpace === s;
            return <button key={s} role="menuitemradio" aria-checked={active} onClick={() => {
              setActiveSpace(s);
              setOpen(null);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/90 hover:bg-white/10 transition-colors text-left" data-id="element-8286" data-mp="3r7oawcywg6ch">
                    <span className="w-4 shrink-0 flex items-center justify-center" data-id="element-8287" data-mp="33kg57txk6v9x">
                      {active && <Check size={15} className="text-white" data-id="element-8288" data-mp="37lrv80kchiin" />}
                    </span>
                    <span className="truncate" data-id="element-8289" data-mp="3cis4slgel3ei">{s}</span>
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Journey picker */}
      <div className="relative" data-id="element-8290" data-mp="2jvpak5irfeu2">
        <button onClick={() => setOpen(open === 'journey' ? null : 'journey')} aria-haspopup="dialog" aria-expanded={open === 'journey'} className={triggerClass(open === 'journey')} data-id="element-8291" data-mp="2jue23seddiwm">
          <Plus size={14} data-id="element-8292" data-mp="3s44m76oiynm7" />
          Journey
        </button>
        <AnimatePresence data-id="element-8293" data-mp="3n631yifd4i95">
          {open === 'journey' && <motion.div initial={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4,
          scale: 0.98
        }} transition={{
          duration: 0.14,
          ease: 'easeOut'
        }} className="absolute left-0 top-full mt-1.5 z-50 w-80 origin-top-left rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 p-2.5" data-id="element-8294" data-mp="3n5a11y334myx">
              <div className="relative mb-1" data-id="element-8295" data-mp="3i78lhn4vb5s7">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8296" data-mp="3i4vtsi9b5h1y" />
                <input ref={searchRef} type="text" value={journeyQuery} onChange={e => setJourneyQuery(e.target.value)} placeholder="Search journeys…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-8297" data-mp="3d9zyn98rydkm" />
              </div>
              <div className="max-h-60 overflow-y-auto pt-1 flex flex-col gap-1" data-id="element-8298" data-mp="151lfrkd7p7x7">
                {filteredJourneys.length === 0 ? <p className="px-3 py-6 text-center text-xs text-neutral-400" data-id="element-8299" data-mp="r2u567xk64dt">
                    No journeys match “{journeyQuery}”.
                  </p> : filteredJourneys.map(j => <button key={j} onClick={() => {
              setOpen(null);
              setJourneyQuery('');
            }} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-800 hover:bg-neutral-100 transition-colors truncate" data-id="element-8300" data-mp="1jponay6wkro4">
                      {j}
                    </button>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
type DashboardVariant = 'current' | 'calm-deferred' | 'progressive' | 'chat-first';
export function Dashboard() {
  // DEBUG: Variant switcher for comparing dashboard directions.
  // Defaults to the Progressive Disclosure direction (first in the toggle).
  const [variant, setVariant] = useState<DashboardVariant>('progressive');
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Deep links: ?view=<navId> opens the dashboard directly on that view, so
  // other surfaces (the web chat's sidebar) can link to the right page rather
  // than dumping the user on the generic overview.
  const viewParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('view') : null;
  const [activeNav, setActiveNav] = useState(viewParam || 'overview');
  // ── Sidebar content vs. sidebar settings ────────────────────────────────
  // Tracks and Chats are the things people open all day, so they live in the
  // sidebar as real lists. The CONNECT / MANAGE / BRAIN nav is configuration
  // you visit occasionally, so it collapses behind one disclosure rather than
  // sitting above the content as 12 permanent rows.
  const [tracksExpanded, setTracksExpanded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [starterCat, setStarterCat] = useState<StarterCategory>('comms');
  const [activePlan, setActivePlan] = useState<StarterPrompt | null>(null);
  // Whether the "Create a pulse" slide-over panel is open.
  const [pulseCreateOpen, setPulseCreateOpen] = useState(false);
  // Whether the "Create a journey" slide-over panel is open.
  const [journeyCreateOpen, setJourneyCreateOpen] = useState(false);
  // Whether the "Create an assistant" slide-over panel is open.
  const [assistantCreateOpen, setAssistantCreateOpen] = useState(false);
  // Selected category in the Recipes filter tabs ('All' = no filter).
  const [recipeCategory, setRecipeCategory] = useState('All');
  // Selected function in the Assistants filter tabs ('All' = no filter).
  const [assistantCategory, setAssistantCategory] = useState('All');
  // ── Chat tab strip (right panel) ────────────────────────────────────────
  // The chat panel hosts one or more open chat sessions as closeable tabs.
  // Closing a tab removes it; if it was the active one, focus falls to the
  // neighboring tab. Closing the LAST tab leaves the panel in a calm empty
  // state ("No active chats") with a button to start a fresh one.
  type ChatTab = {
    id: number;
    label: string;
  };
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([{
    id: 1,
    label: 'New Chat'
  }, {
    id: 2,
    label: 'Session 39200'
  }]);
  const [activeChatId, setActiveChatId] = useState<number>(1);
  const nextChatIdRef = useRef(3);
  // In the "calm-deferred" and "chat-first" directions the persistent right
  // chat panel is hidden on load; it only appears once the user explicitly
  // enters a task. This boolean tracks that deferred-entry state.
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  // In the "progressive" direction the overview swings between a first-time
  // guided setup and the everyday returning dashboard. We lift that state up
  // here so the right chat panel can stay hidden during first-time setup —
  // a brand-new user shouldn't be greeted by a chat they haven't earned yet.
  const [progressiveState, setProgressiveState] = useState<'first' | 'returning'>('first');
  const openNewChat = () => {
    const id = nextChatIdRef.current++;
    setChatTabs(tabs => [...tabs, {
      id,
      label: 'New Chat'
    }]);
    setActiveChatId(id);
    setActivePlan(null);
    setChatPanelOpen(true);
  };
  const closeChat = (id: number) => {
    setChatTabs(tabs => {
      const idx = tabs.findIndex(t => t.id === id);
      const next = tabs.filter(t => t.id !== id);
      // If the closed tab was active, move focus to the neighbor (prefer the
      // tab to the left, falling back to the new first tab).
      if (id === activeChatId) {
        const fallback = next[idx - 1] ?? next[0];
        setActiveChatId(fallback ? fallback.id : -1);
        setActivePlan(null);
      }
      if (next.length === 0) {
        setChatPanelOpen(false);
      }
      return next;
    });
  };
  // Integrations tab controls
  const [intView, setIntView] = useState<'app' | 'list'>('app');
  const [intTab, setIntTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [intQuery, setIntQuery] = useState('');
  // Activity log filters
  const [activityType, setActivityType] = useState<ActivityType | 'all'>('all');
  const [activityStatus, setActivityStatus] = useState<ActivityStatus | 'all'>('all');
  // Timing filter: which date buckets to include in the Activity feed.
  const [activityRange, setActivityRange] = useState<ActivityRange>('all');
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  // Bottom-left account menu + the billing / manage-subscription modal it opens.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const navGroups = [{
    items: [{
      id: 'overview',
      label: 'Home',
      icon: LayoutGrid
    }]
  }, {
    label: 'CONNECT',
    items: [{
      id: 'integrations',
      label: 'Integrations',
      icon: Plug
    }, {
      id: 'power-ups',
      label: 'Power Ups',
      icon: Zap
    }]
  }, {
    label: 'MANAGE',
    items: [{
      id: 'groups',
      label: 'Groups',
      icon: Layers
    }, {
      id: 'spaces-tracks',
      label: 'Tracks',
      icon: Map
    }, {
      id: 'assistants',
      label: 'Assistants',
      icon: UserCircle
    }, {
      id: 'skills',
      label: 'Skills',
      icon: GraduationCap
    }, {
      id: 'journeys',
      label: 'Workflows',
      icon: ListChecks
    }, {
      id: 'pulses',
      label: 'Pulses',
      icon: Activity
    }, {
      id: 'conversations',
      label: 'Chats',
      icon: MessageSquare
    }]
  }, {
    label: 'BRAIN',
    items: [{
      id: 'files',
      label: 'Files',
      icon: FolderOpen
    }, {
      id: 'tables',
      label: 'Tables',
      icon: Layers
    }, {
      id: 'viewer',
      label: 'Viewer',
      icon: Monitor
    }]
  }];
  // Everything except Home lives behind the "Manage workspace" disclosure —
  // Tracks and Chats are surfaced above as real content lists instead.
  const managedNavGroups = navGroups.filter(g => !!g.label);
  const managedNavActive = managedNavGroups.some(g => g.items.some(i => i.id === activeNav)) && !activeTrack && !activeChat;
  // Active tracks only, alphabetical — the sidebar is a shortcut list, not the
  // full filterable library (that's the Tracks view).
  const sidebarTracks = TRACKS.filter(t => t.status === 'active').slice().sort((a, b) => a.name.localeCompare(b.name));
  const integrations = [{
    name: 'AgentMail',
    desc: 'productivity',
    icon: 'gmail',
    connected: false
  }, {
    name: 'Airtable',
    desc: 'productivity',
    icon: 'airtable',
    connected: false
  }, {
    name: 'Anthropic',
    desc: 'Claude models and the Anthropic API.',
    icon: 'anthropic',
    connected: false
  }, {
    name: 'Asana',
    desc: '1 account connected',
    icon: 'asana',
    connected: true
  }, {
    name: 'Attio',
    desc: 'sales',
    icon: 'attio',
    connected: false
  }, {
    name: 'Cloudflare',
    desc: 'infrastructure',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Cloudflare R2',
    desc: 'S3-compatible object storage with …',
    icon: 'cloudflare',
    connected: false
  }, {
    name: 'Crisp',
    desc: '1 account connected',
    icon: 'crisp',
    connected: true
  }, {
    name: 'Custom',
    desc: 'Credentials for services without a d…',
    icon: 'box',
    connected: false
  }, {
    name: 'Expo',
    desc: '1 account connected',
    icon: 'expo',
    connected: true
  }, {
    name: 'Facebook',
    desc: 'marketing',
    icon: 'facebook',
    connected: false
  }, {
    name: 'Folk',
    desc: 'sales',
    icon: 'box',
    connected: false
  }, {
    name: 'GitHub',
    desc: '2 accounts connected',
    icon: 'github',
    connected: true
  }, {
    name: 'Google Gemini',
    desc: 'ai',
    icon: 'googlegemini',
    connected: false
  }, {
    name: 'Google Workspace',
    desc: 'Gmail, Drive, Calendar, Docs, Sheets.',
    icon: 'google',
    connected: false
  }, {
    name: 'Granola',
    desc: 'AI meeting notepad. Search transcri…',
    icon: 'box',
    connected: false
  }, {
    name: 'Growi',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Heroku',
    desc: 'infrastructure',
    icon: 'heroku',
    connected: false
  }, {
    name: 'Linear',
    desc: 'Issue tracking and project managem…',
    icon: 'linear',
    connected: false
  }, {
    name: 'Magic Patterns',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Meta Ads',
    desc: 'Facebook + Instagram ad managem…',
    icon: 'meta',
    connected: false
  }, {
    name: 'Notion',
    desc: 'Pages, databases, and structured d…',
    icon: 'notion',
    connected: false
  }, {
    name: 'OpenAI',
    desc: 'GPT models and the OpenAI API.',
    icon: 'openai',
    connected: false
  }, {
    name: 'OpenRouter',
    desc: 'ai',
    icon: 'box',
    connected: false
  }, {
    name: 'Postgres',
    desc: 'Open-source relational database.',
    icon: 'postgresql',
    connected: false
  }, {
    name: 'RevenueCat',
    desc: '1 account connected',
    icon: 'box',
    connected: true
  }, {
    name: 'Sentry',
    desc: '1 account connected',
    icon: 'sentry',
    connected: true
  }];
  // The handful most teams connect first — a curated discovery shortlist so the
  // long list isn't the only way in. Matched by name against `integrations`.
  const POPULAR_INTEGRATIONS = new Set(['Google Workspace', 'Slack', 'AgentMail', 'Notion', 'GitHub', 'Asana', 'Linear', 'Meta Ads', 'Airtable', 'Attio']);
  return (
    // Flat, calm product surface. We intentionally avoid the
    // `min-h-screen bg-[#FAF8F4]` combination here: a global rule in index.css
    // stamps the marketing halftone dot-texture onto any such container, which
    // made the dashboard read "old school." `h-screen` + an explicit cream
    // canvas keeps the app chrome clean and modern. Using the brand cream
    // (#FAF8F4) — lighter than the previous #F4F2EE — so it reads as a very
    // subtle warm accent rather than a heavy tint.
    <div className={`h-screen bg-[#FAF8F4] flex font-body overflow-hidden ${isDarkMode ? 'dark-dashboard' : ''}`} data-id="element-8301" data-mp="oq5c1zpz3tqn">
      {isDarkMode && <style data-id="element-8302" data-mp="9zy2k9hy1l23">{`
          .dark-dashboard {
            color-scheme: dark;
          }
          .dark-dashboard .bg-white { background-color: #160F0A !important; }
          .dark-dashboard .bg-\\[\\#FAF8F4\\] { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-50 { background-color: #1A120C !important; }
          .dark-dashboard .bg-neutral-100 { background-color: #2B211A !important; }
          .dark-dashboard .bg-neutral-200 { background-color: #6B5D52 !important; }
          .dark-dashboard .bg-\\[\\#2B211A\\] { background-color: #EAE4D9 !important; }
          
          .dark-dashboard .text-neutral-900 { color: #F4F0EA !important; }
          .dark-dashboard .text-neutral-800 { color: #EAE4D9 !important; }
          .dark-dashboard .text-neutral-700 { color: #E7E0D7 !important; }
          .dark-dashboard .text-neutral-600 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-500 { color: #9C8E82 !important; }
          .dark-dashboard .text-neutral-400 { color: #6B5D52 !important; }
          .dark-dashboard .text-\\[\\#2B211A\\] { color: #F4F0EA !important; }
          .dark-dashboard .text-white { color: #160F0A !important; }
          
          .dark-dashboard .border-neutral-100 { border-color: #1A120C !important; }
          .dark-dashboard .border-neutral-200 { border-color: #2B211A !important; }
          .dark-dashboard .border-neutral-300 { border-color: #6B5D52 !important; }
          
          .dark-dashboard .hover\\:bg-neutral-50:hover { background-color: #1A120C !important; }
          .dark-dashboard .hover\\:bg-neutral-100:hover { background-color: #2B211A !important; }
          .dark-dashboard .hover\\:bg-neutral-200:hover { background-color: #6B5D52 !important; }
          .dark-dashboard .hover\\:bg-\\[\\#1A1410\\]:hover { background-color: #D5CFC5 !important; }
          
          .dark-dashboard .hover\\:text-neutral-900:hover { color: #F4F0EA !important; }
          .dark-dashboard .hover\\:text-neutral-700:hover { color: #EAE4D9 !important; }
          .dark-dashboard .hover\\:text-neutral-600:hover { color: #E7E0D7 !important; }
          .dark-dashboard .hover\\:text-\\[\\#2B211A\\]:hover { color: #F4F0EA !important; }
          
          /* Fix simpleicons inversion in dark mode */
          .dark-dashboard img[src*="simpleicons.org"] { filter: brightness(0) invert(1) opacity(0.8); }
        `}</style>}
      {/* Left Sidebar */}
      <div className="w-[240px] bg-white border-r border-[#EFE7DA] flex flex-col shrink-0 h-screen" data-id="element-8303" data-mp="1cd8wgzta1k00">
        {/* Workspace Switcher */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-[#EFE7DA] cursor-pointer hover:bg-[#FAF8F4] transition-colors duration-200 ease-out" data-id="element-8304" data-mp="18achrk6uy81c">
          <div className="flex items-center gap-2.5" data-id="element-8305" data-mp="ydrf35ib990o">
            <div className="w-6 h-6 rounded-lg bg-[#2B211A] flex items-center justify-center text-white text-xs font-bold" data-id="element-8306" data-mp="yej0xom3vsdr">
              G
            </div>
            <span className="font-semibold text-sm text-[#2B211A]" data-id="element-8307" data-mp="omn38enxm4a2">
              Giga Main
            </span>
          </div>
          <ChevronDown size={14} className="text-[#A2917F]" data-id="element-8308" data-mp="kne7j49bzwbv" />
        </div>

        {/* Nav — content first (Tracks, Chats), configuration collapsed below */}
        <div className="flex-1 py-3 px-3 flex flex-col gap-4 overflow-y-auto" data-id="element-8309" data-mp="an3ubjnaqowp">
          {/* Home */}
          <button onClick={() => {
            setActiveNav('overview');
            setActiveTrack(null);
            setActiveChat(null);
          }} className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ease-out ${activeNav === 'overview' && !activeTrack && !activeChat ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A]'}`} data-id="element-8310" data-mp="1yjtmk1g3ycpp">
            <LayoutGrid size={18} strokeWidth={activeNav === 'overview' ? 2.5 : 2} data-id="element-8311" data-mp="1fssn7cb2sf5m" />
            Home
          </button>

          {/* Tracks — the working library. Capped at 12 with a Show more, so a
           long list never pushes Chats out of view. */}
          <div className="flex flex-col gap-0.5" data-id="element-8312" data-mp="16rced8zwsl3i">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-8313" data-mp="1avk3h7aa4329">
              {/* Sentence case, not all-caps letterspaced. A shouted micro-label
                above every group is the loudest dated tell in an app sidebar,
                and it is competing with the items it is labelling. */}
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} className="text-[11.5px] font-bold text-[#A2917F] transition-colors hover:text-[#2B211A]" data-id="element-8314" data-mp="i8sr5xmktmnh">
                Tracks
              </button>
              <button type="button" onClick={() => setActiveNav('spaces-tracks')} aria-label="All tracks" className="text-[#C4B4A2] transition-colors hover:text-[#2B211A]" data-id="element-8315" data-mp="s4dmjcx53oo9">
                <Plus size={13} data-id="element-8316" data-mp="115twxknyfqsh" />
              </button>
            </div>
            {sidebarTracks.slice(0, tracksExpanded ? sidebarTracks.length : 12).map(t => {
              const isActive = activeTrack === t.name;
              return <button key={t.name} onClick={() => {
                setActiveTrack(t.name);
                setActiveChat(null);
                setActiveNav('spaces-tracks');
              }} title={t.name} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-[13.5px] transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A] font-medium'}`} data-id="element-8317" data-mp="1az10czz4wwpx">
                  <Folder size={14} className={isActive ? 'text-white shrink-0' : 'text-[#B6A895] shrink-0'} data-id="element-8318" data-mp="1in0ity3t4vp7" />
                  <span className="truncate" data-id="element-8319" data-mp="1xbn22awk27lp">{t.name}</span>
                </button>;
            })}
            {sidebarTracks.length > 12 && <button type="button" onClick={() => setTracksExpanded(v => !v)} className="px-3 py-1.5 text-left text-[13px] font-medium text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-8320" data-mp="1sj3nbam4t6fb">
                {tracksExpanded ? 'Show less' : `Show ${sidebarTracks.length - 12} more`}
              </button>}
          </div>

          {/* Chats */}
          <div className="flex flex-col gap-0.5" data-id="element-8321" data-mp="2bbfm5ndktpkp">
            <div className="flex items-center justify-between px-3 py-1" data-id="element-8322" data-mp="2bbpbd8qsrspc">
              <button type="button" onClick={() => setActiveNav('conversations')} className="text-[11.5px] font-bold text-[#A2917F] transition-colors hover:text-[#2B211A]" data-id="element-8323" data-mp="lx9el75pru67">
                Chats
              </button>
              <button type="button" onClick={() => setActiveNav('conversations')} aria-label="New chat" className="text-[#C4B4A2] transition-colors hover:text-[#2B211A]" data-id="element-8324" data-mp="2sltcglvbjj78">
                <Plus size={13} data-id="element-8325" data-mp="2ivgsovvbvu25" />
              </button>
            </div>
            {SIDEBAR_CHATS.map(c => {
              const isActive = activeChat === c;
              return <button key={c} onClick={() => {
                setActiveChat(c);
                setActiveTrack(null);
                setActiveNav('conversations');
              }} title={c} className={`flex items-center px-3 py-1.5 rounded-xl text-[13.5px] transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white font-semibold' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A] font-medium'}`} data-id="element-8326" data-mp="2nrfd68djcrxa">
                  <span className="truncate" data-id="element-8327" data-mp="3u9c7g3ibm0su">{c}</span>
                </button>;
            })}
          </div>

          {/* Everything else — one disclosure instead of three always-open
           groups. Opens automatically when one of its views is active. */}
          <div className="mt-auto border-t border-neutral-200 pt-3" data-id="element-8328" data-mp="1kaqym4k8qd2">
            <button type="button" onClick={() => setManageOpen(o => !o)} aria-expanded={manageOpen} className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-8329" data-mp="3srvoj30ryr8x">
              <span className="flex items-center gap-3" data-id="element-8330" data-mp="kd4vsn8gz23k">
                <Settings size={18} strokeWidth={2} data-id="element-8331" data-mp="keprwhafmt3j" />
                Manage workspace
              </span>
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${manageOpen || managedNavActive ? 'rotate-180' : ''}`} data-id="element-8332" data-mp="fg4sul6x2fgo" />
            </button>
            <AnimatePresence initial={false} data-id="element-8333" data-mp="5npb9ozj7swb">
              {(manageOpen || managedNavActive) && <motion.div key="manage-nav" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-8334" data-mp="134xqcm36svde">
                  <div className="flex flex-col gap-3 pt-2" data-id="element-8335" data-mp="1fkdxvlxul19u">
                    {managedNavGroups.map((group, i) => <div key={i} className="flex flex-col gap-0.5" data-id="element-8336" data-mp="1kmmq2g2wbzaw" data-mpi={i}>
                        {group.label && <div className="px-3 py-1 text-[11.5px] font-bold text-[#A2917F]" data-id="element-8337" data-mp="1pjd5r2asgsvs">
                            {group.label}
                          </div>}
                        {group.items.map(item => {
                      const isActive = activeNav === item.id && !activeTrack && !activeChat;
                      return <button key={item.id} onClick={() => {
                        setActiveNav(item.id);
                        setActiveTrack(null);
                        setActiveChat(null);
                      }} className={`flex items-center gap-3 rounded-xl px-3 py-1.5 text-[13.5px] font-medium transition-colors duration-200 ease-out ${isActive ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:bg-[#FAF8F4] hover:text-[#2B211A]'}`} data-id="element-8338" data-mp="23b1j18wrxnec">
                              <item.icon size={16} strokeWidth={isActive ? 2.5 : 2} data-id="element-8339" data-mp="3jqnrvhh70iam" />
                              {item.label}
                            </button>;
                    })}
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* User Profile — clicking the row opens an account menu (above it)
            with a cog affordance and access to Billing / manage subscription. */}
        <div className="relative p-4 border-t border-neutral-200" data-id="element-8340" data-mp="300liwzbbobpe">
          <button type="button" onClick={() => setAccountMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={accountMenuOpen} className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 transition-colors ${accountMenuOpen ? 'bg-neutral-100' : 'hover:bg-neutral-100'}`} data-id="element-8341" data-mp="2vzrum4tywuvz">
            
            <span className="flex items-center gap-3 min-w-0" data-id="element-8342" data-mp="itbpxn9lhbte">
              <span className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-bold text-xs shrink-0" data-id="element-8343" data-mp="nurale7v1gb9">
                F
              </span>
              <span className="flex flex-col min-w-0 text-left" data-id="element-8344" data-mp="dv0fpbfe0zan">
                <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-8345" data-mp="dxd68cz7ohzb">
                  Faith Alqahtani
                </span>
                <span className="text-xs text-neutral-500 truncate" data-id="element-8346" data-mp="16kh31uo5hq00">
                  faith@example.com
                </span>
              </span>
            </span>
            <ChevronsUpDown size={15} className={`shrink-0 transition-colors ${accountMenuOpen ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-8347" data-mp="rtgtfmbo3q2a" />
            
          </button>

          <AnimatePresence data-id="element-8348" data-mp="1kintipqrsnpn">
            {accountMenuOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setAccountMenuOpen(false)} data-id="element-8349" data-mp="1pbqq76qugda2" />
              
                <motion.div role="menu" initial={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 6,
                scale: 0.98
              }} transition={{
                duration: 0.14,
                ease: 'easeOut'
              }} className="absolute bottom-[calc(100%-0.5rem)] left-4 right-4 z-50 origin-bottom overflow-hidden rounded-xl border border-neutral-200 bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.5)]" data-id="element-8350" data-mp="1z5e6wphrq6rl">
                
                  <button role="menuitem" type="button" onClick={() => {
                  setAccountMenuOpen(false);
                  setBillingOpen(true);
                }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-8351" data-mp="12goomy28bc53">
                  
                    <CreditCard size={16} className="text-neutral-500" data-id="element-8352" data-mp="3452li5zoc2bd" />
                    Usage &amp; billing
                  </button>
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#2B211A] transition-colors hover:bg-neutral-100" data-id="element-8353" data-mp="3e3s5c4b4rwu9">
                  
                    <Settings size={16} className="text-neutral-500" data-id="element-8354" data-mp="2lgxmfbtyp5iw" />
                    Settings
                  </button>
                  <div className="my-1 h-px bg-neutral-100" data-id="element-8355" data-mp="2ufstsr8qslns" />
                  <button role="menuitem" type="button" onClick={() => setAccountMenuOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100" data-id="element-8356" data-mp="2ue81l89whet3">
                  
                    <LogOut size={16} className="text-neutral-500" data-id="element-8357" data-mp="26pezeu2oqaq1" />
                    Log out
                  </button>
                </motion.div>
              </>}
          </AnimatePresence>
        </div>
      </div>

      {/* Billing / manage-subscription modal */}
      <BillingModal open={billingOpen} onClose={() => setBillingOpen(false)} data-id="element-8358" data-mp="1a126sh6izhpz" />

      {/* Main Content Column */}
      <div className="flex-1 flex flex-col min-w-0 h-screen" data-id="element-8359" data-mp="151ft66msbzgn">
        {/* Top Bar */}
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shrink-0" data-id="element-8360" data-mp="1j1qhb1wpmzy4">
          <div className="flex items-center gap-4" data-id="element-8361" data-mp="1e3faz15mj7kn">
            <button className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100" data-id="element-8362" data-mp="1e0koz56ypep4">
              <LayoutGrid size={16} className="text-neutral-400" data-id="element-8363" data-mp="1nsytg5pfczez" />
              All Spaces
              <ChevronDown size={14} className="text-neutral-400" data-id="element-8364" data-mp="21t15vceyr0mu" />
            </button>
            <div className="w-px h-4 bg-neutral-200" data-id="element-8365" data-mp="1xro1l6rir5i1" />
            <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8366" data-mp="2gex7z3w5c0nr">
              <Bookmark size={18} data-id="element-8367" data-mp="2gd423qtcxkgt" />
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-8368" data-mp="2ldi0eh55pg1n">
            <div className="relative" data-id="element-8369" data-mp="27erzeqf7uw80">
              <button onClick={() => setWhatsNewOpen(!whatsNewOpen)} className={`p-2 transition-colors rounded-lg ${whatsNewOpen ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'}`} aria-label="What's new" data-id="element-8370" data-mp="27dqnykuag7pr">
                <div className="relative" data-id="element-8371" data-mp="22fp6g4ciggge">
                  <Bell size={18} data-id="element-8372" data-mp="22gql0isqjl2v" />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#EA580C] border border-white" data-id="element-8373" data-mp="1onr5igsydqvb" />
                </div>
              </button>
              <AnimatePresence data-id="element-8374" data-mp="1spm3bbbodm4c">
                {whatsNewOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setWhatsNewOpen(false)} data-id="element-8375" data-mp="1eqmg883t9faa" />
                    <motion.div initial={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 4,
                    scale: 0.98
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute right-0 top-full mt-2 w-80 z-50 bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden" data-id="element-8376" data-mp="1jnmkcdh3o7yr">
                      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50" data-id="element-8377" data-mp="24x4p8cy46ruu">
                        <h3 className="text-sm font-semibold text-neutral-900" data-id="element-8378" data-mp="29pxwp8kyweam">
                          What's new
                        </h3>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto p-2 flex flex-col gap-1" data-id="element-8379" data-mp="2ir5iwsa7looc">
                        {WHATS_NEW.map((n, i) => <div key={i} className="flex items-start gap-3 rounded-lg hover:bg-neutral-50 px-3 py-2.5 transition-colors cursor-pointer" data-id="element-8380" data-mp="2npp60w0jh6ia" data-mpi={i}>
                            <span className={`shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${n.tag === 'New' ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-8381" data-mp="2suasnf6rtlxv">
                              {n.tag}
                            </span>
                            <div data-id="element-8382" data-mp="3gjc65gugnna0">
                              <div className="text-sm font-semibold text-neutral-900" data-id="element-8383" data-mp="3giajef431ccg">
                                {n.title}
                              </div>
                              <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed" data-id="element-8384" data-mp="36tkb6lig8s50">
                                {n.detail}
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors rounded-lg hover:bg-neutral-100" aria-label="Toggle dark mode" data-id="element-8385" data-mp="3uha79kjrtn7k">
              {isDarkMode ? <Sun size={18} data-id="element-8386" data-mp="1xms6tamxybuc" /> : <Moon size={18} data-id="element-8387" data-mp="1iwb6jum1lrz1" />}
            </button>
            <button onClick={openNewChat} className="flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8388" data-mp="1ivrl43x6olg9">
              <Plus size={16} data-id="element-8389" data-mp="19ts43bm65hdb" />
              New chat
            </button>
          </div>
        </header>

        {/* Dashboard Content Area */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-8390" data-mp="zxf98aew0xgz">
          {/* The content surface. Was a 12px-radius card with a cool grey
            border and a flat `shadow-sm`; now the site's own 22px radius, warm
            hairline and two-part shadow, so the app screen reads as the same
            product as the page it is shown on. */}
          <div className="bg-white rounded-[22px] border border-[#EFE7DA] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-16px_rgba(43,33,26,0.12)] min-h-full p-8" data-id="element-8391" data-mp="2z9rhj4wtlsyr">
            {/* Overview — rendered per the active debug variant. */}
            {activeNav === 'overview' && variant === 'current' && <CurrentOverview data-id="element-8392" data-mp="xdiwtuqyvm88" />}
            {activeNav === 'overview' && variant === 'calm-deferred' && <CommandCenterOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-8393" data-mp="skzj8xsa4r3f" />}
            {activeNav === 'overview' && variant === 'progressive' && <EmptyStateOverview onStartTask={openNewChat} onNavigate={setActiveNav} data-id="element-8394" data-mp="16fa3p2x8tz3o" />}
            {activeNav === 'overview' && variant === 'chat-first' && <ChatFirstOverview onStartTask={openNewChat} data-id="element-8395" data-mp="1gak4xofti2ge" />}

            {/* Activity */}
            {activeNav === 'activity' && <ActivityView typeFilter={activityType} setTypeFilter={setActivityType} statusFilter={activityStatus} setStatusFilter={setActivityStatus} rangeFilter={activityRange} setRangeFilter={setActivityRange} data-id="element-8396" data-mp="1bd1u1si8pd5r" />}

            {/* Integrations */}
            {activeNav === 'integrations' && <div className="flex flex-col" data-id="element-8397" data-mp="3ek0uzy6x8sf0">
                <div className="flex items-center justify-between mb-6" data-id="element-8398" data-mp="3enew733umvkv">
                  <div className="flex items-center gap-4" data-id="element-8399" data-mp="3jikhbzpfg67w">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8400" data-mp="3oiownmelmuwj">
                      Integrations
                    </h1>
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8401" data-mp="3tht7sil332pi">
                      <button onClick={() => setIntView('app')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'app' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8402" data-mp="2vvvtyyhnfljw">
                        App View
                      </button>
                      <button onClick={() => setIntView('list')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${intView === 'list' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8403" data-mp="3al0eimbx21v6">
                        List View
                      </button>
                    </div>
                  </div>
                  <CustomCredentialFlow data-id="element-8404" data-mp="v5x04bg4e4d0" />
                </div>

                <div className="relative mb-6" data-id="element-8405" data-mp="v4c74q9geth9">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-8406" data-mp="q6ka5htych67" />
                  <input type="text" value={intQuery} onChange={e => setIntQuery(e.target.value)} placeholder={`Search ${integrations.length} integrations`} className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm focus:outline-none focus:border-neutral-300 transition-colors" data-id="element-8407" data-mp="l77huhkbahw8" />
                </div>

                <div className="flex items-center justify-between mb-6 border-b border-neutral-100" data-id="element-8408" data-mp="7erk9wa6jc2p">
                  <div className="flex items-center gap-6" data-id="element-8409" data-mp="2foeuaqaf3u7">
                    <button onClick={() => setIntTab('all')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'all' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8410" data-mp="3tn0w0pr67ha4">
                      All integrations{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8411" data-mp="3k0zp3e0spt02">
                        {integrations.length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('popular')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'popular' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8412" data-mp="3a2ai4gaftsyl">
                      Popular{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8413" data-mp="6no53ob7uuie">
                        {integrations.filter(x => POPULAR_INTEGRATIONS.has(x.name)).length}
                      </span>
                    </button>
                    <button onClick={() => setIntTab('connected')} className={`text-sm font-medium pb-3 transition-colors ${intTab === 'connected' ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8414" data-mp="kll5ywwpdn2v">
                      Connected{' '}
                      <span className="ml-1 text-neutral-400 font-normal" data-id="element-8415" data-mp="2a03zzqpd4k5x">
                        {integrations.filter(x => x.connected).length}
                      </span>
                    </button>
                  </div>
                </div>

                {(() => {
                const visible = integrations.filter(x => {
                  if (intTab === 'connected' && !x.connected) return false;
                  if (intTab === 'popular' && !POPULAR_INTEGRATIONS.has(x.name)) return false;
                  if (intQuery.trim() && !x.name.toLowerCase().includes(intQuery.toLowerCase())) return false;
                  return true;
                });
                if (visible.length === 0) {
                  return <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-8416" data-mp="3cgp3hczjf1rp">
                        <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-8417" data-mp="37h2r15rj9pjy">
                          <Search size={18} className="text-neutral-400" data-id="element-8418" data-mp="19u9ygusk45c9" />
                        </div>
                        <p className="text-sm font-semibold text-neutral-700" data-id="element-8419" data-mp="1etd5gks3klmv">
                          No integrations found
                        </p>
                        <p className="text-xs text-neutral-500 mt-1" data-id="element-8420" data-mp="1jlo47kb3q1du">
                          Try a different search or switch back to all
                          integrations.
                        </p>
                      </div>;
                }
                if (intView === 'list') {
                  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8421" data-mp="1xkdzcued1qzk">
                        <div className="divide-y divide-neutral-100" data-id="element-8422" data-mp="22hmiax7c1z1v">
                          {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 px-5 py-3.5 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8423" data-mp="37egmseoogo2" data-mpi={i}>
                              <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8424" data-mp="lf615fgi5zi">
                                <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-8425" data-mp="3ryi4sy3dmow8" />
                              </div>
                              <span className="font-semibold text-neutral-900 text-sm shrink-0 w-44 truncate" data-id="element-8426" data-mp="ekn2vro5d20j">
                                {integration.name}
                              </span>
                              <span className="text-xs text-neutral-500 truncate flex-1" data-id="element-8427" data-mp="4nik81tifooh">
                                {integration.desc}
                              </span>
                              {integration.connected ? <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8428" data-mp="2pcye55o0ztic">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-8429" data-mp="1kf0mbzib4q1i" />
                                  Connected
                                </span> : <span className="text-[11px] font-medium text-neutral-400 px-2 py-0.5 shrink-0" data-id="element-8430" data-mp="1fgphk36vd5q5">
                                  Not connected
                                </span>}
                            </div>)}
                        </div>
                      </div>;
                }
                return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8431" data-mp="2361kilblshk0">
                      {visible.map((integration, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer bg-white" data-id="element-8432" data-mp="1y89vbz2cf9ji" data-mpi={i}>
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8433" data-mp="msl9jb0tsgka">
                            <img src={`https://cdn.simpleicons.org/${integration.icon}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-8434" data-mp="2udjyxp6r4gpz" />
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-8435" data-mp="385q28aot2t80">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8436" data-mp="3d2ghwwwp7msw">
                              {integration.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-8437" data-mp="3m3oftdvaylmg">
                              {integration.connected && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-8438" data-mp="3r3sx3641bkdq" />}
                              <span className="text-xs text-neutral-500 truncate" data-id="element-8439" data-mp="1rqos0xgrql43">
                                {integration.desc}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>;
              })()}
              </div>}

            {/* Conversations */}
            {activeNav === 'conversations' && <ConversationsView data-id="element-8440" data-mp="391deyqpmlrao" />}

            {/* Assistants */}
            {activeNav === 'assistants' && (() => {
              // Assistants are grouped by the business FUNCTION they serve
              // (Sales, Marketing, Support, …) — that's how teams reason
              // about who does what — plus a richer card: what it actually
              // does, the tools it works in, and how much it's handled. The
              // function tabs default to "All", mirroring the Recipes filter.
              const ASSISTANTS = [{
                name: 'Sales Assistant',
                desc: 'Handles outbound, logs every touchpoint, and keeps the CRM clean so reps focus on selling.',
                initial: 'S',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '1.2k tasks',
                tools: ['salesforce', 'gmail', 'slack']
              }, {
                name: 'Pipeline Watcher',
                desc: 'Flags stalled deals and at-risk opportunities before the weekly sync.',
                initial: 'P',
                color: 'bg-amber-100 text-amber-700',
                active: true,
                func: 'Sales',
                tasks: '430 tasks',
                tools: ['hubspot', 'slack']
              }, {
                name: 'Content Writer',
                desc: 'Drafts posts, emails, and landing copy in your brand voice, ready for review.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: true,
                func: 'Marketing',
                tasks: '860 tasks',
                tools: ['notion', 'gmail']
              }, {
                name: 'Campaign Analyst',
                desc: 'Pulls campaign metrics into shareable performance recaps.',
                initial: 'C',
                color: 'bg-purple-100 text-purple-700',
                active: false,
                func: 'Marketing',
                tasks: '210 tasks',
                tools: ['mailchimp', 'meta']
              }, {
                name: 'Support Triage',
                desc: 'Tags incoming tickets, drafts replies from help docs, and escalates the rest.',
                initial: 'S',
                color: 'bg-sky-100 text-sky-700',
                active: true,
                func: 'Support',
                tasks: '2.4k tasks',
                tools: ['zendesk', 'slack']
              }, {
                name: 'Finance Analyst',
                desc: 'Reconciles payments to invoices, categorizes expenses, and builds cash snapshots.',
                initial: 'F',
                color: 'bg-rose-100 text-rose-700',
                active: true,
                func: 'Finance',
                tasks: '540 tasks',
                tools: ['stripe', 'quickbooks']
              }, {
                name: 'Research Bot',
                desc: 'Finds and summarizes market and account data on demand.',
                initial: 'R',
                color: 'bg-emerald-100 text-emerald-700',
                active: true,
                func: 'Operations',
                tasks: '690 tasks',
                tools: ['notion', 'google']
              }, {
                name: 'Ops Manager',
                desc: 'Monitors workflows across tools and flags anything that breaks or stalls.',
                initial: 'O',
                color: 'bg-blue-100 text-blue-700',
                active: false,
                func: 'Operations',
                tasks: '310 tasks',
                tools: ['linear', 'slack']
              }, {
                name: 'PR Summarizer',
                desc: 'Digests new pull requests and posts a daily summary to the team.',
                initial: 'P',
                color: 'bg-indigo-100 text-indigo-700',
                active: true,
                func: 'Engineering',
                tasks: '980 tasks',
                tools: ['github', 'slack']
              }];
              const FUNCTIONS = ['All', ...Array.from(new Set(ASSISTANTS.map(a => a.func)))];
              const visible = assistantCategory === 'All' ? ASSISTANTS : ASSISTANTS.filter(a => a.func === assistantCategory);
              return <div className="flex flex-col" data-id="element-8441" data-mp="ip3sur4pcxx9">
                    <div className="flex items-start justify-between gap-4 mb-5" data-id="element-8442" data-mp="dq0vlsuyrvzu">
                      <div data-id="element-8443" data-mp="seexa0gn9eet">
                        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8444" data-mp="sen7tlpao7mu">
                          Assistants
                        </h1>
                        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8445" data-mp="nmdkmdfkdlo9">
                          Dedicated AI teammates, each focused on one part of
                          the business — with their own instructions, tools, and
                          memory. Filter by function to find the right one.
                        </p>
                      </div>
                      <button onClick={() => setAssistantCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8446" data-mp="2benuod4gt5s3">
                        <Plus size={16} data-id="element-8447" data-mp="2pcun3dqnadkd" />
                        New assistant
                      </button>
                    </div>

                    {/* Function filter tabs — defaults to "All" */}
                    <div role="tablist" aria-label="Filter assistants by function" className="flex flex-wrap gap-2 mb-6" data-id="element-8448" data-mp="2gc67iells55d">
                      {FUNCTIONS.map(f => {
                    const count = f === 'All' ? ASSISTANTS.length : ASSISTANTS.filter(a => a.func === f).length;
                    const active = assistantCategory === f;
                    return <button key={f} role="tab" aria-selected={active} onClick={() => setAssistantCategory(f)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8449" data-mp="18zdxaxb7k80l">
                            {f}
                            <span className={`ml-1.5 ${active ? 'text-white/60' : 'text-neutral-400'}`} data-id="element-8450" data-mp="1rnljbdenqa5x">
                              {count}
                            </span>
                          </button>;
                  })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8451" data-mp="2fc4qtqpvuiwy">
                      {visible.map((a, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer bg-white" data-id="element-8452" data-mp="2aisdwdukg0k1" data-mpi={i}>
                          <div className="flex items-start justify-between mb-3" data-id="element-8453" data-mp="331pj507xesiw">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base ${a.color}`} data-id="element-8454" data-mp="2y9o10xo2u7ky">
                              {a.initial}
                            </div>
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${a.active ? 'text-emerald-600 bg-emerald-50' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-8455" data-mp="3al8abtrbps2k">
                              <span className={`w-1.5 h-1.5 rounded-full ${a.active ? 'bg-emerald-500' : 'bg-neutral-400'}`} data-id="element-8456" data-mp="3jlmxkynulh8p" />
                              {a.active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1" data-id="element-8457" data-mp="24lw13g0h1fi">
                            <h3 className="font-semibold text-neutral-900 text-sm" data-id="element-8458" data-mp="26gf0c7q2jgy">
                              {a.name}
                            </h3>
                            <span className="text-[10px] font-medium text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded-full" data-id="element-8459" data-mp="b637g8ih4exv">
                              {a.func}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-8460" data-mp="1ivdbyieagcnl">
                            {a.desc}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-neutral-100" data-id="element-8461" data-mp="18vw9e9uc2evu">
                            <div className="flex items-center -space-x-1.5" data-id="element-8462" data-mp="1dupkxwq27vtd">
                              {a.tools.map((tool, t) => <span key={t} className="w-6 h-6 rounded-md bg-white border border-neutral-200 flex items-center justify-center" title={tool} data-id="element-8463" data-mp="21ka3wfm7qt2n" data-mpi={t}>
                                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3.5 h-3.5 object-contain" alt="" onError={e => {
                            ;
                            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                          }} data-id="element-8464" data-mp="1ruqmzfi299af" />
                                </span>)}
                            </div>
                            <span className="text-[11px] font-medium text-neutral-400" data-id="element-8465" data-mp="dtuavj5gqgxm">
                              {a.tasks}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
            })()}

            {/* Skills */}
            {activeNav === 'skills' && <div className="flex flex-col" data-id="element-8466" data-mp="illybktbo6jj">
                <div className="flex flex-wrap items-start justify-between gap-6 mb-6" data-id="element-8467" data-mp="3r0m5cqxb8eer">
                  <div className="min-w-[280px] flex-1" data-id="element-8468" data-mp="3m1suz7dbl3it">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8469" data-mp="3m3dnysjzkeek">
                      Skills
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8470" data-mp="3ttmhisqlamk">
                      Reusable capabilities you invoke with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-600" data-id="element-8471" data-mp="1fa1qlontgu0c">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                  <div className="flex items-center flex-wrap gap-4 shrink-0" data-id="element-8472" data-mp="80ycuto76yph">
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8473" data-mp="cwdorg2nakj9">
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8474" data-mp="h0tqr69517t7">
                        Private (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-8475" data-mp="3lejla2j6bwaq">
                        Global (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8476" data-mp="3ue6jygkiqo03">
                        Space (0)
                      </button>
                    </div>
                    <div className="w-px h-6 bg-neutral-200" data-id="element-8477" data-mp="31opy61rem5cu" />
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5" data-id="element-8478" data-mp="36ouf1sw8523l">
                      <button className="px-3 py-1.5 text-sm font-medium bg-white rounded-md shadow-sm text-neutral-900 font-bold" data-id="element-8479" data-mp="36mhowsgbblfg">
                        Custom (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8480" data-mp="2u4fcy1u25g50">
                        Giga (0)
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8481" data-mp="2l5af6xqblqtw">
                        Community (0)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8482" data-mp="26fw3hiz9uxbc">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100" data-id="element-8483" data-mp="21i2vqliblv8y">
                    <div className="flex items-center gap-2" data-id="element-8484" data-mp="1xg93n1m6xzke">
                      <button className="p-1 text-neutral-400 hover:text-neutral-600 border-b-2 border-neutral-900 text-neutral-900" data-id="element-8485" data-mp="1sgwe293xqee4">
                        <LayoutGrid size={16} data-id="element-8486" data-mp="3siw16i14gpvl" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-8487" data-mp="ea6a08kdxv7">
                      <span className="text-xs font-medium text-neutral-400" data-id="element-8488" data-mp="3itcdmz2gs7ue">
                        Filter by
                      </span>
                      <div className="flex items-center bg-neutral-100 rounded-md p-0.5" data-id="element-8489" data-mp="3nrdztsv6sna3">
                        <button className="px-2.5 py-1 text-xs font-medium bg-white rounded shadow-sm text-neutral-900" data-id="element-8490" data-mp="38u3nyiemtfdu">
                          None
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8491" data-mp="2zv01r5k64jv4">
                          Tag
                        </button>
                        <button className="px-2.5 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-700" data-id="element-8492" data-mp="y33s61hk9k2z">
                          Pack
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-8493" data-mp="1d6n1di1nriyx">
                    <h3 className="text-lg font-semibold text-neutral-400 mb-2" data-id="element-8494" data-mp="188ljv1jvrrpk">
                      No skills yet
                    </h3>
                    <p className="text-sm text-neutral-400" data-id="element-8495" data-mp="1h7qkcdf05v4d">
                      Invoke them with{' '}
                      <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-neutral-500" data-id="element-8496" data-mp="1vwcve2xlk0ps">
                        /skill-name
                      </code>
                      .
                    </p>
                  </div>
                </div>
              </div>}

            {/* Journeys */}
            {activeNav === 'journeys' && <div className="flex flex-col" data-id="element-8497" data-mp="20wr6jtuhvqs1">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8498" data-mp="20tljsl9c90zh">
                  <div data-id="element-8499" data-mp="ajs6jooy9dte">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8500" data-mp="al3c9u1ygvn5">
                      Journeys
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8501" data-mp="25myd1wbjzi6h">
                      Ongoing projects you and your assistant work through
                      together over time — with shared context and
                      back-and-forth as things evolve, not a one-off task.
                    </p>
                  </div>
                  <button onClick={() => setJourneyCreateOpen(true)} className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8502" data-mp="2khmft2pkk2ie">
                    <Plus size={16} data-id="element-8503" data-mp="2ybds2iuoq0ht" />
                    Create journey
                  </button>
                </div>

                <div className="flex items-center justify-between mb-6" data-id="element-8504" data-mp="33agmzagyaqc2">
                  <div className="flex items-center gap-3" data-id="element-8505" data-mp="3899xqv4us38j">
                    <button className="p-1 text-neutral-400 hover:text-neutral-600" data-id="element-8506" data-mp="38b4mkkj8wfhw">
                      <LayoutGrid size={18} data-id="element-8507" data-mp="3cbog7e4m10za" />
                    </button>
                    <div className="flex items-center gap-1.5" data-id="element-8508" data-mp="10ns23xin334n">
                      {[{
                      initials: 'GM',
                      count: 19,
                      color: 'bg-sky-700'
                    }, {
                      initials: 'GG',
                      count: 8,
                      color: 'bg-neutral-800'
                    }, {
                      initials: 'NC',
                      count: 3,
                      color: 'bg-indigo-600'
                    }, {
                      initials: 'Ev',
                      count: 2,
                      color: 'bg-rose-800'
                    }, {
                      initials: 'NT',
                      count: 1,
                      color: 'bg-neutral-900'
                    }, {
                      initials: 'NO',
                      count: 1,
                      color: 'bg-rose-700'
                    }].map((a, i) => <div key={i} className="relative" data-id="element-8509" data-mp="1yaicjprbjteo" data-mpi={i}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${a.color} ring-2 ring-white`} data-id="element-8510" data-mp="16nz5ro9opnot">
                            {a.initials}
                          </div>
                          <div className="absolute -top-2 -right-1.5 bg-white text-neutral-500 text-[9px] font-bold px-1 rounded-full border border-neutral-200" data-id="element-8511" data-mp="xo2dlco366p0">
                            {a.count}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer" data-id="element-8512" data-mp="1uc04y6kz3rrk">
                    <input type="checkbox" className="rounded border-neutral-300 text-[#2B211A] focus:ring-[#2B211A]" data-id="element-8513" data-mp="1gd0in5kxnoyk" />
                    Show favourites
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8514" data-mp="4wsfb1xog9lu">
                  <div className="divide-y divide-neutral-100" data-id="element-8515" data-mp="9z17vx6n19nf">
                    {[{
                    author: 'NC',
                    color: 'bg-indigo-600',
                    name: 'Ryan Paid Ads',
                    desc: "Updated Jun 20 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Getting Giga Out There',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Viktor Product Updates',
                    desc: "Updated Jun 19 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Product Backlog',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Giga Creator Outreach',
                    desc: "Updated Jun 18 '26",
                    status: 'Unset'
                  }, {
                    author: 'E',
                    color: 'bg-rose-700',
                    name: 'Twitter Linkedin Content',
                    desc: "Updated Jun 17 '26",
                    status: 'Unset'
                  }, {
                    author: 'GG',
                    color: 'bg-neutral-800',
                    name: 'Newsjacking For Giga Content',
                    desc: "Updated Jun 16 '26",
                    status: 'Unset'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Early User Activation',
                    desc: "Created Jun 16 '26 · Updated Jun 16 '26",
                    status: 'In focus',
                    statusColor: 'text-emerald-600'
                  }, {
                    author: 'GM',
                    color: 'bg-sky-700',
                    name: 'Getting Operator Stable',
                    desc: "Updated May 22 '26",
                    status: 'Unset'
                  }].map((j, i) => <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer group" data-id="element-8516" data-mp="195b6o3tjqbeo" data-mpi={i}>
                        <div className="flex items-center gap-4 min-w-0" data-id="element-8517" data-mp="1e5h3dyeoao3m">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${j.color}`} data-id="element-8518" data-mp="21u0d8iddf8xt">
                            {j.author}
                          </div>
                          <div className="flex flex-col min-w-0" data-id="element-8519" data-mp="1s1ku3hqc8ebc">
                            <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8520" data-mp="1x0xjoa8lfzhm">
                              {j.name}
                            </span>
                            <span className="text-xs text-neutral-400 truncate" data-id="element-8521" data-mp="2pj1enlnsdjtl">
                              {j.desc}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0" data-id="element-8522" data-mp="2uh03zocuh2pw">
                          <span className={`text-xs font-medium ${j.statusColor || 'text-neutral-400'}`} data-id="element-8523" data-mp="2pg2kn8g3m2mt">
                            {j.status}
                          </span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8524" data-mp="2gfxfz62ahicc">
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8525" data-mp="2boxjg1x312ad">
                              <Info size={16} data-id="element-8526" data-mp="26qvyfbg3isw9" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8527" data-mp="21s2kjhwwczvk">
                              <Star size={16} data-id="element-8528" data-mp="1wr553d39ujxr" />
                            </button>
                            <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100" data-id="element-8529" data-mp="1srmo2sekmt07">
                              <MoreHorizontal size={16} data-id="element-8530" data-mp="8ade0yqj7fdl" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Pulses */}
            {activeNav === 'pulses' && <div className="flex flex-col" data-id="element-8531" data-mp="m5z9nor9sxez">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8532" data-mp="3kqh1otke5nb3">
                  <div data-id="element-8533" data-mp="3poitc2vvuuy6">
                    <div className="flex items-center gap-2" data-id="element-8534" data-mp="3pptya612e8qv">
                      <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8535" data-mp="3unvi4t6bec3e">
                        Pulses
                      </h1>
                      <span className="text-sm text-neutral-400 font-medium" data-id="element-8536" data-mp="39hesyh4khei">
                        1
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8537" data-mp="31xdd3mm160nc">
                      Recurring tasks Giga runs on a schedule.
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0" data-id="element-8538" data-mp="1cfq2sx2mshd9">
                    <button className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-8539" data-mp="18ibst374m5k7">
                      <Settings size={16} data-id="element-8540" data-mp="1hhfbi62dsiy6" />
                      Pulse Settings
                    </button>
                    <button onClick={() => setPulseCreateOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 hover:text-neutral-700 transition-colors" data-id="element-8541" data-mp="1w5juzc99cyi0">
                      <Plus size={16} data-id="element-8542" data-mp="256hqf65mrxoz" />
                      New pulse
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mb-4" data-id="element-8543" data-mp="2086i4zuzhv8v">
                  <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer" data-id="element-8544" data-mp="asgxf1wri4wt">
                    <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-8545" data-mp="5u5jog3cja9b" />
                    Show disabled
                  </label>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-8546" data-mp="2mglr4i68pm6c">
                  <div className="px-6 py-5 flex items-center justify-between gap-4" data-id="element-8547" data-mp="28pgvdr5zte1a">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-8548" data-mp="2cpqzeya85udi">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" data-id="element-8549" data-mp="2cnwg1oelqabj" />
                      <div className="flex flex-col min-w-0" data-id="element-8550" data-mp="1yskmvlg3grwq">
                        <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-8551" data-mp="23qm6q8lcgv99">
                          No Contact Daily Brief
                        </span>
                        <span className="text-xs text-neutral-400 truncate" data-id="element-8552" data-mp="1tqv7xupr3u3d">
                          Daily at 07:00 Etc/UTC
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-5 shrink-0" data-id="element-8553" data-mp="3o5pg9obn9kpa">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-neutral-400" data-id="element-8554" data-mp="10wyqeaouoehk">
                        <Clock size={14} data-id="element-8555" data-mp="vxvo2y098mda" />
                        in 19h
                      </div>
                      <button className="flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 transition-colors" data-id="element-8556" data-mp="rw0l7p2oe0xe">
                        <Play size={14} data-id="element-8557" data-mp="myiiigf94hxu" />
                        Run
                      </button>
                      <span className="text-sm font-medium text-neutral-900" data-id="element-8558" data-mp="1jmyccope9dgr">
                        On
                      </span>
                      <div className="flex items-center gap-1" data-id="element-8559" data-mp="1fo8r69y46rmk">
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-8560" data-mp="1atlauyd9l3j2">
                          <Pencil size={16} data-id="element-8561" data-mp="23bgwgrvjm0ol" />
                        </button>
                        <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md hover:bg-neutral-100 transition-colors" data-id="element-8562" data-mp="23djtvwz0yw3c">
                          <Trash2 size={16} data-id="element-8563" data-mp="25wesi0ejp6qo" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Power Ups */}
            {activeNav === 'power-ups' && (() => {
              const POWER_UPS = [{
                name: 'Journey Manager',
                desc: 'Uses the Giga Primitives (Journeys, integrations, pulses) to be an extremely...',
                tag: 'on journey',
                icon: LayoutTemplate
              }, {
                name: 'Process Emails',
                desc: 'Archives non-human emails',
                tag: 'Email',
                icon: LayoutTemplate
              }, {
                name: 'Process Meeting Notes',
                desc: 'Meeting Note Taker',
                tag: 'Meeting Note Taker',
                icon: LayoutTemplate
              }, {
                name: 'Product Backlog Manager',
                desc: 'Manages your product backlog',
                tag: 'on journey',
                icon: LayoutTemplate
              }];
              return <div className="flex flex-col" data-id="element-8564" data-mp="3sv5r8nebbvqu">
                  <div className="flex items-center gap-2 mb-2" data-id="element-8565" data-mp="3syjt7uj2e2xr">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8566" data-mp="bmr2hmdz7vz7">
                      Power Ups
                    </h1>
                    <span className="text-sm text-neutral-400 font-medium" data-id="element-8567" data-mp="6n4rzkpj8tu3">
                      {POWER_UPS.length}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 leading-relaxed mb-6 max-w-xl" data-id="element-8568" data-mp="3a70va23wdf2l">
                    Ready-made automations. Pick a power up and we'll set it up with you.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8569" data-mp="3k2auyj6tpad7">
                    {POWER_UPS.map((p, i) => <div key={i} className="flex flex-col p-5 rounded-xl border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all bg-white" data-id="element-8570" data-mp="2wcglmblpq3xp" data-mpi={i}>
                        <div className="flex items-start gap-4 mb-4" data-id="element-8571" data-mp="16w4g9p7hvygb">
                          <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center text-neutral-400 shrink-0" data-id="element-8572" data-mp="1bnmaluf0j0sb">
                            <p.icon size={20} data-id="element-8573" data-mp="wwwv4fuiqzin" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1" data-id="element-8574" data-mp="11ym4lr23f54m">
                            <h3 className="font-semibold text-neutral-900 text-sm mb-1 leading-snug" data-id="element-8575" data-mp="1poovw4y6o726">
                              {p.name}
                            </h3>
                            <p className="text-xs text-neutral-500 leading-relaxed" data-id="element-8576" data-mp="1gn8uunt9dham">
                              {p.desc}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-5 mt-auto" data-id="element-8577" data-mp="57k89xjx2kac">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full" data-id="element-8578" data-mp="995itc8zamhd">
                            {p.tag === 'Email' ? <Paperclip size={10} className="mr-0.5" data-id="element-8579" data-mp="34hdmk4xf1hqk" /> : null}
                            {p.tag}
                          </span>
                        </div>

                        <button className="w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8580" data-mp="3dgilhccw3d39">
                          <Download size={16} data-id="element-8581" data-mp="2lqe5rr02re8d" />
                          Install
                        </button>
                      </div>)}
                  </div>
                </div>;
            })()}

            {/* Groups — the access boundary for tracks, files and tables */}
            {activeNav === 'groups' && <GroupsView data-id="element-8582" data-mp="2zh98smnw7141" />}

            {/* Tracks */}
            {activeNav === 'spaces-tracks' && <TracksView data-id="element-8583" data-mp="2200x7zjg4grp" />}

            {/* Tables */}
            {activeNav === 'tables' && <TablesView data-id="element-8584" data-mp="26yc90ftax1ck" />}


            {/* Files */}
            {activeNav === 'files' && <div className="flex flex-col" data-id="element-8585" data-mp="1zge4yqtw2igq">
                <div className="flex items-start justify-between gap-4 mb-6" data-id="element-8586" data-mp="1ze1nees1ma47">
                  <div data-id="element-8587" data-mp="1uhkp4rr4qfde">
                    <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8588" data-mp="1ueykp0zjcfze">
                      Files
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8589" data-mp="1lgmsiuj0ta4u">
                      A shared workspace for documents — the files you upload
                      for Giga to work from, and the reports, exports, and
                      deliverables it creates for you.
                    </p>
                  </div>
                  <button className="flex items-center gap-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shrink-0" data-id="element-8590" data-mp="16s03g13ld40f">
                    <Plus size={16} data-id="element-8591" data-mp="xph2xnna33hc" />
                    Upload
                  </button>
                </div>
                <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8592" data-mp="ss7ikmeqxi0d">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wide text-neutral-400" data-id="element-8593" data-mp="j3abdfp1wsyp">
                    <span data-id="element-8594" data-mp="96xj8m95mn62">Name</span>
                    <span className="hidden sm:block w-20 text-right" data-id="element-8595" data-mp="98086omx2fjc">
                      Size
                    </span>
                    <span className="w-28 text-right" data-id="element-8596" data-mp="11ukwfg83ibio">Modified</span>
                  </div>
                  <div className="divide-y divide-neutral-100" data-id="element-8597" data-mp="11wxgq01bsxyw">
                    {[{
                    name: 'Reports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 14'
                  }, {
                    name: 'Exports',
                    type: 'folder',
                    size: '—',
                    modified: 'Jun 12'
                  }, {
                    name: 'meeting-notes-jun-12.pdf',
                    type: 'file',
                    size: '184 KB',
                    modified: 'Jun 12'
                  }, {
                    name: 'q3-forecast.xlsx',
                    type: 'file',
                    size: '52 KB',
                    modified: 'Jun 10'
                  }, {
                    name: 'brand-guidelines.pdf',
                    type: 'file',
                    size: '3.1 MB',
                    modified: 'Jun 8'
                  }, {
                    name: 'invoices-184.zip',
                    type: 'file',
                    size: '8.4 MB',
                    modified: 'Jun 6'
                  }].map((f, i) => <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3.5 items-center hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8598" data-mp="30cn0zumfsk8e" data-mpi={i}>
                        <div className="flex items-center gap-3 min-w-0" data-id="element-8599" data-mp="2vfmsld2475e4">
                          <div className="w-8 h-8 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0" data-id="element-8600" data-mp="fkb7ky0fdinz">
                            {f.type === 'folder' ? <Folder size={16} className="text-neutral-400 fill-neutral-200" data-id="element-8601" data-mp="15yati1zjvhln" /> : <FileText size={16} className="text-neutral-400" data-id="element-8602" data-mp="1aw2jurkjqvnq" />}
                          </div>
                          <span className="text-sm font-medium text-neutral-900 truncate" data-id="element-8603" data-mp="1fuvzoqnb2yr2">
                            {f.name}
                          </span>
                        </div>
                        <span className="hidden sm:block w-20 text-right text-xs text-neutral-500" data-id="element-8604" data-mp="1owdapuluwa8w">
                          {f.size}
                        </span>
                        <span className="w-28 text-right text-xs text-neutral-400" data-id="element-8605" data-mp="1tt3qshxnv5ub">
                          {f.modified}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Viewer */}
            {activeNav === 'viewer' && <div className="flex flex-col h-full" data-id="element-8606" data-mp="28hzyuezpwiux">
                <div className="mb-6" data-id="element-8607" data-mp="2ckwim90zul0g">
                  <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8608" data-mp="2cly12ykca9s7">
                    Viewer
                  </h1>
                  <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8609" data-mp="3t252xlwhi10p">
                    A focused, full-screen preview pane. Open any file or
                    conversation here to read it in detail without leaving the
                    dashboard.
                  </p>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center min-h-[320px] text-center border border-dashed border-neutral-200 rounded-xl" data-id="element-8610" data-mp="km23g3fizing">
                  <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-8611" data-mp="fpbrnsar797u">
                    <Monitor size={26} className="text-neutral-400" data-id="element-8612" data-mp="au5utyftca4z" />
                  </div>
                  <h2 className="text-lg font-bold text-neutral-900 mb-2" data-id="element-8613" data-mp="5vbed35bixo1">
                    Nothing to preview yet
                  </h2>
                  <p className="text-neutral-500 max-w-sm text-sm leading-relaxed" data-id="element-8614" data-mp="yll5cehqtxrb">
                    Select a file or conversation and it will open here for a
                    full-screen preview.
                  </p>
                </div>
              </div>}
          </div>
        </main>
      </div>

      {/* Right Chat Panel — persistent in "current" and "progressive"; in the
                                                                                                           calm-deferred and chat-first directions it stays hidden until the user
                                                                                                           explicitly enters a task (chatPanelOpen). In "progressive" it also stays
                                                                                                           hidden during first-time setup so a brand-new user isn't greeted by a
                                                                                                           chat — it appears once they finish setup, switch to the returning
                                                                                                           dashboard, or explicitly start a task (chatPanelOpen). */}
      {(variant === 'current' || variant === 'progressive' && progressiveState === 'returning' || chatPanelOpen) && <div className="w-[320px] lg:w-[380px] bg-white border-l border-neutral-200 flex flex-col shrink-0 h-screen hidden md:flex" data-id="element-8615" data-mp="mb2ptghj731c">
          {/* Chat Tabs */}
          <div className="h-14 flex items-end gap-px px-2 border-b border-neutral-200 bg-neutral-50/50 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-8616" data-mp="w5upqqyl61za">
            {chatTabs.map(tab => {
            const isActive = tab.id === activeChatId;
            return <div key={tab.id} onClick={() => {
              setActiveChatId(tab.id);
              setActivePlan(null);
            }} className={`shrink-0 px-4 py-2 flex items-center gap-3 text-sm font-medium cursor-pointer transition-colors ${isActive ? 'bg-white border border-neutral-200 border-b-0 rounded-t-lg text-neutral-900 relative top-px' : 'text-neutral-500 hover:text-neutral-700'}`} data-id="element-8617" data-mp="1168yycbxbhyw">
                  {tab.label}
                  <button onClick={e => {
                e.stopPropagation();
                closeChat(tab.id);
              }} aria-label={`Close ${tab.label}`} className="text-neutral-400 hover:text-neutral-600" data-id="element-8618" data-mp="axxsow8gp2x">
                    <X size={14} data-id="element-8619" data-mp="96xbwta424pp" />
                  </button>
                </div>;
          })}
            <button onClick={openNewChat} aria-label="New chat" className="shrink-0 ml-1 mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-8620" data-mp="38m0xs7qx3vum">
              <Plus size={15} data-id="element-8621" data-mp="3mjh6xr0lm3q2" />
            </button>
            {/* In the deferred directions, let the user collapse the panel back
             out of view so chat truly stays out of the way until summoned. */}
            {(variant === 'calm-deferred' || variant === 'chat-first') && <button onClick={() => setChatPanelOpen(false)} aria-label="Hide chat panel" title="Hide chat" className="shrink-0 ml-auto mb-1.5 flex items-center justify-center w-6 h-6 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/70 transition-colors" data-id="element-8622" data-mp="11vb7719temxk">
                <ChevronRight size={16} data-id="element-8623" data-mp="125sp77u976f8" />
              </button>}
          </div>

          {/* Chat Content Area */}
          <div className="flex-1 p-4 flex flex-col overflow-y-auto" data-id="element-8624" data-mp="y47ffvd0n899">
            {chatTabs.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-id="element-8625" data-mp="t4ui2goiqisf">
                <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4" data-id="element-8626" data-mp="o9xbrhiminj8">
                  <MessageSquare size={24} className="text-neutral-400" data-id="element-8627" data-mp="jakncsc3t8ej" />
                </div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1" data-id="element-8628" data-mp="f9imk72jgoag">
                  No active chats
                </h3>
                <p className="text-xs text-neutral-500 leading-relaxed max-w-[15rem] mb-5" data-id="element-8629" data-mp="29knpf95swwoq">
                  You closed your open chats. Start a new one to ask Giga to get
                  something done.
                </p>
                <button onClick={openNewChat} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8630" data-mp="3hdsyk1ljo77a">
                  <Plus size={16} data-id="element-8631" data-mp="3qehbsu3463j4" />
                  New chat
                </button>
              </div> : activePlan ? <PlanView prompt={activePlan} onBack={() => setActivePlan(null)} data-id="element-8632" data-mp="2tr317dk550t0" /> : <>
                <div className="flex items-center justify-between mb-6" data-id="element-8633" data-mp="2ypec7rm69hct">
                  <ChatContextControls data-id="element-8634" data-mp="32py6mnfd26v9" />
                  <button className="text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8635" data-mp="32owu0eip5cbf">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8636" data-mp="37i7snsdcums9">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-8637" data-mp="2ezu30g26a749"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-8638" data-mp="27hmipv7iovfx"></path>
                    </svg>
                  </button>
                </div>

                {/* Get moving — starter prompts based on connected tools */}
                <div className="flex items-center gap-2 mb-1.5" data-id="element-8639" data-mp="1yev5pqyztrm6">
                  <Sparkles size={15} className="text-[#EA580C]" data-id="element-8640" data-mp="1ti4nawzpujxl" />
                  <h3 className="text-sm font-semibold text-[#2B211A]" data-id="element-8641" data-mp="1oi8kv26o30k5">
                    Not sure where to start?
                  </h3>
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed mb-4" data-id="element-8642" data-mp="1jpz000kesqoq">
                  Here are a few things you can ask, based on the tools you’ve
                  connected.
                </p>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-8643" data-mp="15rhf0alw1sai">
                  {STARTER_CATEGORIES.map(cat => {
                const isActive = starterCat === cat.id;
                return <button key={cat.id} onClick={() => setStarterCat(cat.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-[#2B211A] text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8644" data-mp="10uqw7fip8ile">
                        {cat.label}
                      </button>;
              })}
                </div>

                {/* Prompt suggestions */}
                <div className="flex flex-col gap-2.5" data-id="element-8645" data-mp="m7s9fxusecd0">
                  {STARTER_PROMPTS[starterCat].map((prompt, i) => <button key={i} onClick={() => setActivePlan(prompt)} className="group text-left p-3.5 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8646" data-mp="19xw43fpzz1rv" data-mpi={i}>
                      <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-8647" data-mp="154jpzziy2ddd">
                        <span className="text-sm font-semibold text-[#2B211A]" data-id="element-8648" data-mp="1sue1acnm7tvi">
                          {prompt.title}
                        </span>
                        <div className="flex items-center -space-x-1.5 shrink-0" data-id="element-8649" data-mp="1xlw3t55ae2il">
                          {prompt.tools.slice(0, 3).map((tool, t) => <span key={t} className="w-5 h-5 rounded-md bg-white border border-neutral-200 flex items-center justify-center" data-id="element-8650" data-mp="3i9gcgky3pbbj" data-mpi={t}>
                              <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-3 h-3 object-contain" alt="" onError={e => {
                        ;
                        (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                      }} data-id="element-8651" data-mp="3s1uezfx06lyr" />
                            </span>)}
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 leading-relaxed mb-2.5" data-id="element-8652" data-mp="38dv5e8e6dkux">
                        {prompt.desc}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#EA580C] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8653" data-mp="34agmn9yuqqds">
                        Try it
                        <ArrowRight size={12} data-id="element-8654" data-mp="jkzlgr23idxc" />
                      </span>
                    </button>)}
                </div>
              </>}
          </div>

          {/* Chat Input — hidden when there are no open chats */}
          {chatTabs.length > 0 && <div className="p-4 border-t border-neutral-200 bg-white" data-id="element-8655" data-mp="1nwnk2fl4wdoz">
              <div className="flex items-end gap-2" data-id="element-8656" data-mp="1nygve86ozm3b">
                <button className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-8657" data-mp="1swibqlcqh7b3">
                  <Paperclip size={20} data-id="element-8658" data-mp="1xtsawspxcxc4" />
                </button>
                <div className="flex-1 relative" data-id="element-8659" data-mp="21vn93ock6ulo">
                  <textarea placeholder="Enter your message" className="w-full border border-neutral-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none min-h-[44px] max-h-[120px]" rows={1} data-id="element-8660" data-mp="21uc8tyibo4zb" />
                </div>
                <button className="p-2.5 text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mb-1" data-id="element-8661" data-mp="2gmmhay9hfh4q">
                  <Send size={18} data-id="element-8662" data-mp="2li1qve0giqvc" />
                </button>
              </div>
            </div>}
        </div>}

      {/* Create-a-pulse slide-over */}
      <PulseCreatePanel open={pulseCreateOpen} onClose={() => setPulseCreateOpen(false)} data-id="element-8663" data-mp="4xg9f4noyx99" />

      {/* Create-a-journey slide-over */}
      <JourneyCreatePanel open={journeyCreateOpen} onClose={() => setJourneyCreateOpen(false)} data-id="element-8664" data-mp="3w4iynqjyytgu" />

      {/* Create-an-assistant slide-over */}
      <AssistantCreatePanel open={assistantCreateOpen} onClose={() => setAssistantCreateOpen(false)} data-id="element-8665" data-mp="snahl8x2fxn6" />

      {/* Persistent Onboarding Widget */}
      <OnboardingWidget onNavigate={setActiveNav} data-id="element-8666" data-mp="npigppeg116u" />

      {/* Persistent "Book a call" — always available bottom-right for anyone
                   who's stuck or hasn't set anything up yet. Sits above the debug
                   cycler so the two don't overlap. */}
      <a href="/talk-to-sales" className="fixed bottom-16 right-5 z-50 inline-flex items-center gap-2 h-11 pl-4 pr-5 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold shadow-xl transition-colors" data-id="element-8667" data-mp="1bebelremlj43">
        <PhoneCall size={16} className="text-[#EA580C]" data-id="element-8668" data-mp="30w8bdujv2rf4" />
        Book a call
      </a>

      {/* DEBUG: Variant cycler — one subtle button, bottom-right. Each tap
                                                                           advances to the next dashboard direction. A short label flashes on
                                                                           hover / right after a change so you know which one you're seeing,
                                                                           then fades away to stay out of the way. */}
      <VariantCycler variant={variant} setVariant={setVariant} data-id="element-8669" data-mp="286rnna76ryp8" />
    </div>
  );
}
// One small, low-key control for flipping between dashboard directions while
// designing. Stays tucked in the corner; reveals the current variant's name on
// hover or for a beat after each tap, otherwise it's just a dot.
const DASHBOARD_VARIANTS: {
  id: DashboardVariant;
  label: string;
}[] = [{
  id: 'progressive',
  label: 'Progressive Disclosure'
}, {
  id: 'calm-deferred',
  label: 'Calm + Deferred Chat'
}, {
  id: 'chat-first',
  label: 'Chat-First'
}, {
  id: 'current',
  label: 'Current'
}];
function VariantCycler({
  variant,
  setVariant
}: {
  variant: DashboardVariant;
  setVariant: (v: DashboardVariant) => void;
}) {
  const [hovered, setHovered] = useState(false);
  // Briefly reveal the label right after a tap, then let it fade.
  const [justChanged, setJustChanged] = useState(false);
  const current = DASHBOARD_VARIANTS.find(v => v.id === variant);
  const cycle = () => {
    const idx = DASHBOARD_VARIANTS.findIndex(v => v.id === variant);
    const next = DASHBOARD_VARIANTS[(idx + 1) % DASHBOARD_VARIANTS.length];
    setVariant(next.id);
    setJustChanged(true);
    window.setTimeout(() => setJustChanged(false), 1400);
  };
  const showLabel = hovered || justChanged;
  return <button onClick={cycle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} title="Switch dashboard variant" aria-label={`Dashboard variant: ${current?.label}. Click to switch.`} className="fixed bottom-5 right-5 z-[9999] flex items-center gap-2 h-8 pl-2.5 pr-2.5 rounded-full bg-[#2B211A]/80 hover:bg-[#2B211A] text-white/70 hover:text-white backdrop-blur-sm border border-white/10 shadow-lg transition-all duration-300" data-id="element-8670" data-mp="p7troqpc92zw">
      <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] shrink-0" data-id="element-8671" data-mp="i2hjrs0kvxet" />
      <span className={`overflow-hidden whitespace-nowrap text-[11px] font-medium tracking-wide transition-all duration-300 ${showLabel ? 'max-w-[200px] opacity-100' : 'max-w-0 opacity-0'}`} data-id="element-8672" data-mp="3gly6za3a0x1n">
        {current?.label}
      </span>
    </button>;
}
// ── Shared Overview data ─────────────────────────────────────────────────────
// Reused across the dashboard direction variants so each mockup reads as the
// same product with the same underlying data — only the layout/hierarchy
// changes between directions.
const OVERVIEW_STATS = [{
  label: 'Tasks Completed',
  value: '14,231',
  note: '+12% this week',
  tone: 'good' as const
}, {
  label: 'Hours Saved',
  value: '342',
  note: '~14 days of work',
  tone: 'good' as const
}, {
  label: 'Active Automations',
  value: '12',
  note: 'Running smoothly',
  tone: 'neutral' as const
}];
const RECENT_ACTIVITY = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  tool: 'notion',
  time: '10 mins ago'
}, {
  task: 'Pulled account history from HubSpot',
  tool: 'hubspot',
  time: '12 mins ago'
}, {
  task: 'Replied to support ticket #4092',
  tool: 'zendesk',
  time: '1 hour ago'
}, {
  task: 'Summarized weekly sync meeting',
  tool: 'slack',
  time: '3 hours ago'
}, {
  task: 'Updated 14 stale deals in CRM',
  tool: 'salesforce',
  time: '5 hours ago'
}];
// Full activity log — the browsable, filterable record behind the Overview's
// "Recent Activity" teaser. Mixes chats, pulses, journeys, recipes, and
// assistants, with outcomes so failures surface.
const ACTIVITY_LOG: ActivityEntry[] = [{
  task: 'Drafted Q3 renewal brief for Northwind',
  detail: 'Pulled the account history and assembled a one-page brief.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'notion',
  daysAgo: 0,
  time: '10 mins ago'
}, {
  task: 'Morning briefing delivered',
  detail: 'Calendar, unread mail, and tasks due today, sent at 8:00am.',
  type: 'Pulse',
  status: 'success',
  source: 'Daily Briefing',
  tool: 'gmail',
  daysAgo: 0,
  time: '12 mins ago'
}, {
  task: 'Enriching 6 new inbound leads',
  detail: 'Filling in firmographics and scoring against your ICP.',
  type: 'Recipe',
  status: 'running',
  source: 'Lead Enrichment',
  tool: 'hubspot',
  daysAgo: 0,
  time: '20 mins ago'
}, {
  task: 'Could not post recap to #leadership',
  detail: 'Slack returned a permissions error — reconnect may be needed.',
  type: 'Pulse',
  status: 'needs-attention',
  source: 'Weekly Report',
  tool: 'slack',
  daysAgo: 0,
  time: '1 hour ago'
}, {
  task: 'Replied to support ticket #4092',
  detail: 'Drafted from help docs and sent after your approval.',
  type: 'Assistant',
  status: 'success',
  source: 'Support Bot',
  tool: 'zendesk',
  daysAgo: 0,
  time: '2 hours ago'
}, {
  task: 'Checked in on “Early User Activation”',
  detail: 'Summarized progress since last week and proposed next steps.',
  type: 'Journey',
  status: 'success',
  source: 'Early User Activation',
  tool: 'notion',
  daysAgo: 0,
  time: '3 hours ago'
}, {
  task: 'Summarized weekly sync meeting',
  detail: 'Action items captured and assigned to owners.',
  type: 'Chat',
  status: 'success',
  source: 'You',
  tool: 'slack',
  daysAgo: 1,
  time: '4:12 PM'
}, {
  task: 'Opened GitHub issue for Sentry spike',
  detail: 'Bundled the stack trace and tagged the likely owner.',
  type: 'Recipe',
  status: 'success',
  source: 'Bug Triage',
  tool: 'github',
  daysAgo: 1,
  time: '1:48 PM'
}, {
  task: 'Updated 14 stale deals in CRM',
  detail: 'Refreshed stages and logged notes after each touchpoint.',
  type: 'Assistant',
  status: 'success',
  source: 'Sales Assistant',
  tool: 'salesforce',
  daysAgo: 1,
  time: '11:30 AM'
}, {
  task: 'Invoice chase paused — missing connection',
  detail: 'QuickBooks token expired before reminders went out.',
  type: 'Recipe',
  status: 'needs-attention',
  source: 'Invoice Chaser',
  tool: 'quickbooks',
  daysAgo: 1,
  time: '9:05 AM'
}, {
  task: 'Compiled the weekly leadership digest',
  detail: 'Pulled last week’s metrics and posted the summary to #leadership.',
  type: 'Recipe',
  status: 'success',
  source: 'Weekly Report',
  tool: 'notion',
  daysAgo: 3,
  time: 'Mon, 9:00 AM'
}, {
  task: 'Onboarded a new closed-won deal',
  detail: 'Spun up the client workspace and sent the welcome sequence.',
  type: 'Recipe',
  status: 'success',
  source: 'Deal Onboarding',
  tool: 'gmail',
  daysAgo: 4,
  time: 'Mon, 2:30 PM'
}, {
  task: 'Swept 9 stale Linear issues',
  detail: 'Nudged each owner with their list of issues idle 14+ days.',
  type: 'Pulse',
  status: 'success',
  source: 'Stale Issue Sweep',
  tool: 'linear',
  daysAgo: 5,
  time: 'Tue, 8:15 AM'
}, {
  task: 'Generated monthly marketing performance report',
  detail: 'Aggregated spend and conversion metrics across all channels.',
  type: 'Recipe',
  status: 'success',
  source: 'Monthly Marketing Report',
  tool: 'google',
  daysAgo: 12,
  time: '9:00 AM'
}, {
  task: 'Processed 42 invoices from Stripe',
  detail: 'Matched payments to open invoices and updated the ledger.',
  type: 'Assistant',
  status: 'success',
  source: 'Finance Assistant',
  tool: 'stripe',
  daysAgo: 28,
  time: '4:30 PM'
}, {
  task: 'Audited Meta Ads spend',
  detail: 'Paused 3 underperforming ad sets and exported the summary.',
  type: 'Recipe',
  status: 'success',
  source: 'Ad Spend Audit',
  tool: 'meta',
  daysAgo: 75,
  time: '10:15 AM'
}, {
  task: 'Set up new hire onboarding for Alex',
  detail: 'Created Notion workspace, requested tool access, and queued intros.',
  type: 'Journey',
  status: 'success',
  source: 'New Hire Onboarding',
  tool: 'notion',
  daysAgo: 210,
  time: '9:00 AM'
}];
const ACTIVITY_TYPE_FILTERS: (ActivityType | 'all')[] = ['all', 'Chat', 'Pulse', 'Journey', 'Recipe', 'Assistant'];
// Small status pill + icon, reused per activity row.
function ActivityStatusBadge({
  status
}: {
  status: ActivityStatus;
}) {
  if (status === 'running') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8673" data-mp="2471q5ftv8aba">
        <Loader2 size={11} className="animate-spin" data-id="element-8674" data-mp="1z75lrfh9agv7" />
        Running
      </span>;
  }
  if (status === 'needs-attention') {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8675" data-mp="2d5m3e1gnprs4">
        <AlertTriangle size={11} data-id="element-8676" data-mp="it5q4gpg1ddr" />
        Needs attention
      </span>;
  }
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-8677" data-mp="es3q3xnpcxaq">
      <CheckCircle2 size={11} data-id="element-8678" data-mp="4pgi2mwko6gi" />
      Done
    </span>;
}
// The Activity view — a filterable, grouped audit feed of everything Giga did.
const RANGE_MAX_DAYS: Record<Exclude<ActivityRange, 'custom'>, number> = {
  all: Infinity,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '6m': 180,
  '12m': 365
};
function getBucketForDaysAgo(daysAgo: number): string {
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo <= 6) return 'This week';
  return 'Earlier';
}
function ActivityView({
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  rangeFilter,
  setRangeFilter
}: {
  typeFilter: ActivityType | 'all';
  setTypeFilter: (t: ActivityType | 'all') => void;
  statusFilter: ActivityStatus | 'all';
  setStatusFilter: (s: ActivityStatus | 'all') => void;
  rangeFilter: ActivityRange;
  setRangeFilter: (r: ActivityRange) => void;
}) {
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const filtered = ACTIVITY_LOG.filter(e => {
    if (typeFilter !== 'all' && e.type !== typeFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (rangeFilter === 'custom') {
      // In a real app, we'd parse dates. Here we just show everything if custom is selected but empty.
      // If dates are provided, we'd calculate daysAgo. For prototype, we'll just let it pass.
      return true;
    } else {
      const maxDays = RANGE_MAX_DAYS[rangeFilter];
      if (e.daysAgo > maxDays) return false;
    }
    return true;
  });
  const stats = {
    total: filtered.length,
    success: filtered.filter(e => e.status === 'success').length,
    running: filtered.filter(e => e.status === 'running').length,
    needsAttention: filtered.filter(e => e.status === 'needs-attention').length
  };
  const buckets = ['Today', 'Yesterday', 'This week', 'Earlier'];
  return <div className="flex flex-col" data-id="element-8679" data-mp="26kxivji6y3wl">
      <div className="mb-6" data-id="element-8680" data-mp="26jw351q8ct8j">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8681" data-mp="2dxdgnib1jekp">Activity</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-xl leading-relaxed" data-id="element-8682" data-mp="2dz80suehn2nq">
          Everything Giga has done across your chats, pulses, journeys, recipes,
          and assistants — with the outcome of each run.
        </p>
      </div>

      {/* Filter-aware summary strip */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-8683" data-mp="2ss1j0nl59ww3">
        <div className="flex-1 min-w-[120px]" data-id="element-8684" data-mp="2wtn1qpkd15e7">
          <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1" data-id="element-8685" data-mp="31u153ub0nr5w">
            Total
          </div>
          <div className="text-2xl font-bold text-neutral-900" data-id="element-8686" data-mp="36m185vmdv06p">
            {stats.total}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8687" data-mp="3bmouw53kwuv4">
          <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1" data-id="element-8688" data-mp="3gki4l843c705">
            Done
          </div>
          <div className="text-2xl font-bold text-emerald-700" data-id="element-8689" data-mp="3kltttcj1yq7t">
            {stats.success}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8690" data-mp="9sxu83smln01">
          <div className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1" data-id="element-8691" data-mp="5q14zu8kysn4">
            Running
          </div>
          <div className="text-2xl font-bold text-amber-700" data-id="element-8692" data-mp="joirxpqnw13z">
            {stats.running}
          </div>
        </div>
        <div className="flex-1 min-w-[120px]" data-id="element-8693" data-mp="enuzr0qp5e86">
          <div className="text-xs font-medium text-rose-600 uppercase tracking-wide mb-1" data-id="element-8694" data-mp="3n4zmrn5uybk1">
            Needs Attention
          </div>
          <div className="text-2xl font-bold text-rose-700" data-id="element-8695" data-mp="3i893ys2o51ux">
            {stats.needsAttention}
          </div>
        </div>
      </div>

      {/* Filters: type tabs + status dropdown */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-id="element-8696" data-mp="3s42iui3v17fv">
        <div role="tablist" aria-label="Filter activity by type" className="flex flex-wrap gap-2" data-id="element-8697" data-mp="34ehz3wzvu65j">
          {ACTIVITY_TYPE_FILTERS.map(t => {
          const active = typeFilter === t;
          return <button key={t} role="tab" aria-selected={active} onClick={() => setTypeFilter(t)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-[#2B211A] text-white shadow-sm' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`} data-id="element-8698" data-mp="1exw1thh27jft">
                {t === 'all' ? 'All' : t}
              </button>;
        })}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-8699" data-mp="1xom69ama3eas">
          {/* Timing filter — slice the feed by when it happened. */}
          <div className="flex items-center gap-2" data-id="element-8700" data-mp="22nyu9yovyrey">
            <div className="relative" data-id="element-8701" data-mp="22lmadfzki6z9">
              <Clock size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8702" data-mp="1ooh2dagamy1d" />
              <select aria-label="Filter activity by time" value={rangeFilter} onChange={e => setRangeFilter(e.target.value as ActivityRange)} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-8703" data-mp="1tlpil5hvsrzz">
                <option value="all" data-id="element-8704" data-mp="sf6brf34vfxy">All time</option>
                <option value="7d" data-id="element-8705" data-mp="xglw14xhlifa">Last 7 days</option>
                <option value="30d" data-id="element-8706" data-mp="xdzqtby2jf08">Last 30 days</option>
                <option value="90d" data-id="element-8707" data-mp="iia1b3tocjqr">Last 90 days</option>
                <option value="6m" data-id="element-8708" data-mp="nhwfbfhbu40m">Last 6 months</option>
                <option value="12m" data-id="element-8709" data-mp="nglf1pn3bee9">Last 12 months</option>
                <option value="custom" data-id="element-8710" data-mp="9q87hoe421pg">Custom range</option>
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8711" data-mp="eh80wjo0u1n7" />
            </div>
            {rangeFilter === 'custom' && <div className="flex items-center gap-2" data-id="element-8712" data-mp="3vvvculjy5xg8">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-8713" data-mp="4sq4eemrygpd" />
                <span className="text-neutral-400 text-sm" data-id="element-8714" data-mp="38akxiwblyjcr">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300" data-id="element-8715" data-mp="38b4d46dscvnm" />
              </div>}
          </div>
          {/* Status filter — slice by outcome. */}
          <div className="relative" data-id="element-8716" data-mp="3r9gpe0zzulnf">
            <Filter size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8717" data-mp="3r8f310dj2cqe" />
            <select aria-label="Filter activity by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as ActivityStatus | 'all')} className="appearance-none border border-neutral-200 rounded-lg pl-8 pr-9 py-2 text-sm font-medium text-neutral-700 bg-white focus:outline-none focus:border-neutral-300 cursor-pointer" data-id="element-8718" data-mp="3m9ubdpccd1dk">
              <option value="all" data-id="element-8719" data-mp="8vh672c1800g">All statuses</option>
              <option value="success" data-id="element-8720" data-mp="8x0rx8ggj39m">Done</option>
              <option value="running" data-id="element-8721" data-mp="32npc5mha83xz">Running</option>
              <option value="needs-attention" data-id="element-8722" data-mp="32pa5x9vrvius">Needs attention</option>
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-8723" data-mp="36pkaci3x217j" />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? <div className="border border-neutral-200 rounded-xl px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-8724" data-mp="3gflz7c43fnni">
          <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-8725" data-mp="3lg1aly4tel2u">
            <History size={18} className="text-neutral-400" data-id="element-8726" data-mp="3uev4vhuoazd8" />
          </div>
          <p className="text-sm font-semibold text-neutral-700" data-id="element-8727" data-mp="3ucsgf2965eam">
            Nothing matches these filters
          </p>
          <p className="text-xs text-neutral-500 mt-1" data-id="element-8728" data-mp="200u770av0ieq">
            Try a different type, time range, or status.
          </p>
        </div> : <div className="flex flex-col gap-6" data-id="element-8729" data-mp="250gj96eybsly">
          {buckets.map(bucket => {
        const items = filtered.filter(e => getBucketForDaysAgo(e.daysAgo) === bucket);
        if (items.length === 0) return null;
        return <div key={bucket} data-id="element-8730" data-mp="1so4pnvnk5wtd">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-neutral-400 mb-2" data-id="element-8731" data-mp="1nqcnm8t78ubg">
                  {bucket}
                </h2>
                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white" data-id="element-8732" data-mp="1isktr98zuy4m">
                  <div className="divide-y divide-neutral-100" data-id="element-8733" data-mp="2gdpayl76p65t">
                    {items.map((e, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8734" data-mp="2bgyuvxvdqake" data-mpi={i}>
                        <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8735" data-mp="26f166o47mnk2">
                          <img src={`https://cdn.simpleicons.org/${e.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={ev => {
                    ;
                    (ev.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                  }} data-id="element-8736" data-mp="v2741pdvtbd7" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1" data-id="element-8737" data-mp="2joa2tuag1z3h">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-8738" data-mp="24z42g2zve2u0">
                            <span className="font-semibold text-neutral-900 text-sm" data-id="element-8739" data-mp="29xow1jkm9o9h">
                              {e.task}
                            </span>
                            <ActivityStatusBadge status={e.status} data-id="element-8740" data-mp="2xnswji2id7w9" />
                          </div>
                          <p className="text-xs text-neutral-500 leading-relaxed mt-0.5" data-id="element-8741" data-mp="32luicarbjlbf">
                            {e.detail}
                          </p>
                          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-neutral-400" data-id="element-8742" data-mp="2snn9ygb3ibl1">
                            <span className="inline-flex items-center gap-1 font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full" data-id="element-8743" data-mp="1d76e4wsvt9oh">
                              {e.type}
                            </span>
                            <span data-id="element-8744" data-mp="19o78e9kp3pb4">·</span>
                            <span className="truncate" data-id="element-8745" data-mp="19rbjzs0hpnbt">{e.source}</span>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-8746" data-mp="1jny5mj0n4iew">
                          {e.time}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
}
const WHATS_NEW = [{
  tag: 'New',
  title: 'Pulses now support custom schedules',
  detail: 'Run recurring tasks on any cadence you define.'
}, {
  tag: 'Improved',
  title: 'Faster Slack + HubSpot sync',
  detail: 'Context updates land in under a second.'
}, {
  tag: 'New',
  title: 'Journeys keep context across check-ins',
  detail: 'Pick up long-running projects right where you left off.'
}];
function StatCard({
  label,
  value,
  note,
  tone
}: {
  label: string;
  value: string;
  note: string;
  tone: 'good' | 'neutral';
}) {
  return <div className="bg-white p-6 rounded-xl border border-neutral-200" data-id="element-8747" data-mp="sx2c46mh9wqc">
      <div className="text-neutral-500 text-sm font-medium mb-2" data-id="element-8748" data-mp="wydsrmndj7me">{label}</div>
      <div className="text-4xl font-bold text-neutral-900" data-id="element-8749" data-mp="11q5nm9dkbzic">{value}</div>
      <div className={`mt-2 text-xs font-medium inline-flex px-2 py-0.5 rounded-full ${tone === 'good' ? 'text-[#0F9D58] bg-[#0F9D58]/10' : 'text-neutral-500 bg-neutral-100'}`} data-id="element-8750" data-mp="11ryrxi0pe7n6">
        {note}
      </div>
    </div>;
}
function RecentActivityList() {
  return <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-8751" data-mp="e1cmzzlzx3he">
      <div className="divide-y divide-neutral-100" data-id="element-8752" data-mp="iyuy9wnhjusk">
        {RECENT_ACTIVITY.map((item, i) => <div key={i} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-8753" data-mp="nyh8dx80oux5" data-mpi={i}>
            <div className="flex items-center gap-4" data-id="element-8754" data-mp="1ueb8cw4m9oi6">
              <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8755" data-mp="1zd4jihwfl3f6">
                <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" data-id="element-8756" data-mp="3jux0kx8l1l11" />
              </div>
              <span className="font-medium text-neutral-900 text-sm" data-id="element-8757" data-mp="3a5ltfg59aqfs">
                {item.task}
              </span>
            </div>
            <span className="text-xs font-medium text-neutral-400 sm:text-right" data-id="element-8758" data-mp="355r0l71x7qs3">
              {item.time}
            </span>
          </div>)}
      </div>
    </div>;
}
// ── Direction: Current (unchanged baseline) ──────────────────────────────────
function CurrentOverview() {
  return <>
      <div className="flex items-center justify-between mb-8" data-id="element-8759" data-mp="2r67z2x3m7dor">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-8760" data-mp="2m7y16eqi9yzg">Overview</h1>
        <div className="relative hidden md:block" data-id="element-8761" data-mp="2m9rbc40bv1c7">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-8762" data-mp="pfwgx9s0zgq9" />
          <input type="text" placeholder="Search activity..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-64" data-id="element-8763" data-mp="kgbgeux7j0bf" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10" data-id="element-8764" data-mp="3uhssb99cfyje">
        {OVERVIEW_STATS.map(s => <StatCard key={s.label} {...s} data-id="element-8765" data-mp="3gmguuu3sztys" />)}
      </div>
      <div data-id="element-8766" data-mp="3kotrmt0kohjf">
        <h2 className="font-bold text-neutral-900 text-lg mb-4" data-id="element-8767" data-mp="3kobuzva001az">
          Recent Activity
        </h2>
        <RecentActivityList data-id="element-8768" data-mp="36oiz7ljm6uw5" />
      </div>
    </>;
}
// ── Direction #1: Command Center ─────────────────────────────────────────────
// A state-aware dashboard for returning users. Surfaces failures/needs-attention
// items at the top, provides clickable stats routing to real destinations, and
// shows a cross-product heartbeat of what Giga has been doing.
function CommandCenterOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (nav: string) => void;
}) {
  // In a real app, this would be derived from the activity log or system state.
  // We hardcode a single failure here to demonstrate the "needs attention" state.
  const needsAttention = [{
    task: 'Could not post recap to #leadership',
    detail: 'Slack returned a permissions error — reconnect may be needed.',
    tool: 'slack'
  }];
  const reducedMotion = useReducedMotion();
  const hasAttention = needsAttention.length > 0;
  // "Happening now" — the tools Giga is actively touching, shown as a live
  // logo strip so you can SEE work in motion rather than read a list.
  const happeningNow = [{
    slug: 'hubspot',
    label: 'Enriching 6 leads',
    live: true
  }, {
    slug: 'gmail',
    label: 'Drafting 2 replies',
    live: true
  }, {
    slug: 'notion',
    label: 'Updating brief',
    live: false
  }, {
    slug: 'slack',
    label: 'Posting recap',
    live: false
  }];
  // Visual-only week rhythm: tasks completed per day, Mon→Sun.
  const weekRhythm = [{
    day: 'M',
    value: 42
  }, {
    day: 'T',
    value: 61
  }, {
    day: 'W',
    value: 38
  }, {
    day: 'T',
    value: 74
  }, {
    day: 'F',
    value: 88
  }, {
    day: 'S',
    value: 29
  }, {
    day: 'S',
    value: 18
  }];
  const peak = Math.max(...weekRhythm.map(d => d.value));
  // ── Tasks: work Giga has teed up that needs YOUR call ────────────────────
  // Distinct from "Recent activity" (what Giga already finished on its own).
  // Tasks are the handful of things Giga paused on — a draft to approve, a
  // charge to confirm, a reconnect — so the human stays in the loop. Each
  // routes to where it gets resolved.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review drafts',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  // ── Recent activity: a cross-product feed of finished work ───────────────
  const recentHeartbeat = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    type: 'Chat',
    tool: 'gmail',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    type: 'Journey',
    tool: 'hubspot',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    type: 'Pulse',
    tool: 'slack',
    status: 'done',
    time: '1h ago'
  }, {
    task: 'Updated the Q3 launch brief',
    type: 'Recipe',
    tool: 'notion',
    status: 'done',
    time: '3h ago'
  }, {
    task: 'Filed 8 receipts and reconciled 2 payments',
    type: 'Assistant',
    tool: 'stripe',
    status: 'done',
    time: 'Yesterday'
  }];
  const ease = (delay: number) => reducedMotion ? {
    duration: 0
  } : {
    duration: 0.45,
    delay,
    ease: 'easeOut' as const
  };
  return <div className="max-w-5xl mx-auto py-2" data-id="element-8769" data-mp="19pxk4nbnz1fb">
      {/* ── Hero status module ─────────────────────────────────────────────
                                         One strong visual anchor on a warm dark stage (the on-brand surface
                                         used across the marketing site), with a faint radial glow + dot grid.
                                         It folds the greeting and the system state into a single focal point
                                         and carries the primary "Start a task" action. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0)} className="relative overflow-hidden rounded-3xl bg-[#1A1410] px-6 py-8 md:px-10 md:py-10 mb-6" data-id="element-8770" data-mp="3uhg58ej2j5fh">
        {/* Faint warm glow — the only gradient allowed, on the dark stage. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        background: hasAttention ? 'radial-gradient(70% 60% at 80% 0%, rgba(225,29,72,0.18) 0%, transparent 70%)' : 'radial-gradient(70% 60% at 80% 0%, rgba(234,88,12,0.16) 0%, transparent 70%)'
      }} data-id="element-8771" data-mp="3l3pgbdqx1fzb" />
        {/* Subtle dot grid texture. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
        maskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(120% 120% at 80% 0%, #000 30%, transparent 75%)'
      }} data-id="element-8772" data-mp="2uyd0gkk7tdu" />

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-7" data-id="element-8773" data-mp="2io5pg0densxd">
          <div className="max-w-xl" data-id="element-8774" data-mp="2yvbn36sokl6d">
            <div className="flex items-center gap-2 mb-3" data-id="element-8775" data-mp="2ywd8o57boq2c">
              <span className={`relative flex h-2 w-2 ${hasAttention ? '' : ''}`} data-id="element-8776" data-mp="2u4lihvs2wmcq">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${reducedMotion ? '' : 'animate-ping'} ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-8777" data-mp="2u2ikaogrvmwx" />
                <span className={`relative inline-flex h-2 w-2 rounded-full ${hasAttention ? 'bg-rose-400' : 'bg-emerald-400'}`} data-id="element-8778" data-mp="2k88nmukss2ge" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-8779" data-mp="3cujl3wd3xn8z">
                {hasAttention ? `${needsAttention.length} item${needsAttention.length > 1 ? 's' : ''} need attention` : 'All systems healthy'}
              </span>
            </div>
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-8780" data-mp="1wgtjqxddhd31">
              Good morning, Faith
            </p>
            <h1 className="text-3xl md:text-[2.5rem] font-bold text-white tracking-tight leading-[1.08]" data-id="element-8781" data-mp="1rh8hoe2woom3">
              {hasAttention ? 'One thing needs a quick fix.' : "You're all caught up."}
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-white/60" data-id="element-8782" data-mp="by0xbo6ss8jx">
              {hasAttention ? 'Everything else is running smoothly. Clear the item below and Giga keeps moving.' : 'Giga has been working in the background. Pick up where you left off or start something new.'}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-8783" data-mp="qmn8ddpe6e5c">
              <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-white hover:bg-neutral-100 text-[#1A1410] px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8784" data-mp="up0dq1088a1l">
                <Sparkles size={16} className="text-[#EA580C]" data-id="element-8785" data-mp="14n7rkaz7ybzd" />
                Start a task
              </button>
              <button onClick={() => onNavigate('activity')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/75 hover:text-white transition-colors" data-id="element-8786" data-mp="31gjekifhyqlj">
                View all activity
                <ArrowRight size={15} data-id="element-8787" data-mp="2yf6j6z5m1fuv" />
              </button>
            </div>
          </div>

          {/* Hero side panel — either the focused needs-attention card, or a
           calm week-rhythm sparkline so the hero always carries a visual. */}
          <div className="w-full md:w-[300px] shrink-0" data-id="element-8788" data-mp="33d7yra3tux1l">
            {hasAttention ? <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-8789" data-mp="33bwvrci7htbj">
                <div className="flex items-center gap-2 mb-3" data-id="element-8790" data-mp="3hawkek5py87p">
                  <div className="w-7 h-7 rounded-lg bg-rose-500/20 border border-rose-400/30 flex items-center justify-center shrink-0" data-id="element-8791" data-mp="3dacpzocj5ip9">
                    <AlertTriangle size={14} className="text-rose-300" data-id="element-8792" data-mp="3r4wtsy819zm8" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-rose-300" data-id="element-8793" data-mp="3m5smye8l0dz2">
                    Needs attention
                  </span>
                </div>
                {needsAttention.map((item, i) => <div key={i} data-id="element-8794" data-mp="3w02gw0d69kbw" data-mpi={i}>
                    <div className="flex items-center gap-2.5 mb-1.5" data-id="element-8795" data-mp="ji1e67vrh1um">
                      <span className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shrink-0" data-id="element-8796" data-mp="njwcr4mb514p">
                        <img src={`https://cdn.simpleicons.org/${item.tool}/e11d48`} className="w-4 h-4 object-contain" alt="" data-id="element-8797" data-mp="9kdgb92uze88" />
                      </span>
                      <p className="text-sm font-semibold text-white leading-snug" data-id="element-8798" data-mp="ehdeyyxdpepb">
                        {item.task}
                      </p>
                    </div>
                    <p className="text-xs text-white/55 leading-relaxed mb-3" data-id="element-8799" data-mp="4m26gyckd3q0">
                      {item.detail}
                    </p>
                    <button onClick={() => onNavigate('integrations')} className="w-full bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors" data-id="element-8800" data-mp="3mwx53p2r6h05">
                      Reconnect Slack
                    </button>
                  </div>)}
              </div> : <div className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-4" data-id="element-8801" data-mp="3hz5eqzhrb2y2">
                <div className="flex items-center justify-between mb-3" data-id="element-8802" data-mp="1ag3xlw79qb5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-white/55" data-id="element-8803" data-mp="3thnzrkh57zks">
                    This week
                  </span>
                  <span className="text-xs font-semibold text-emerald-300" data-id="element-8804" data-mp="3jifc19cvfh6v">
                    350 tasks
                  </span>
                </div>
                <div className="flex items-end justify-between gap-1.5 h-20" data-id="element-8805" data-mp="3ekn7nfv1i2ls">
                  {weekRhythm.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-8806" data-mp="39r2k6hr2or0u" data-mpi={i}>
                      <motion.div initial={reducedMotion ? false : {
                  height: 0
                }} animate={{
                  height: `${d.value / peak * 100}%`
                }} transition={{
                  duration: reducedMotion ? 0 : 0.5,
                  delay: reducedMotion ? 0 : 0.25 + i * 0.05,
                  ease: 'easeOut'
                }} className={`w-full rounded-sm ${i === 4 ? 'bg-[#EA580C]' : 'bg-white/25'}`} style={{
                  minHeight: 4
                }} data-id="element-8807" data-mp="35ra83v2h02qq" />
                      <span className="text-[10px] font-medium text-white/40" data-id="element-8808" data-mp="2lqqt84pfr347">
                        {d.day}
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </motion.section>

      {/* ── Happening now ──────────────────────────────────────────────────
                                         A live tool strip — see Giga working, don't read about it. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.08)} className="mb-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-id="element-8809" data-mp="27xiy4325bphr">
        <div className="flex items-center gap-2 mb-4" data-id="element-8810" data-mp="1f82arjtduyse">
          <Loader2 size={14} className={`text-[#EA580C] ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-8811" data-mp="1k7562cjk9qn6" />
          <h2 className="text-sm font-semibold text-neutral-900" data-id="element-8812" data-mp="2kjx3dgsn4719">
            Happening now
          </h2>
          <span className="text-xs text-neutral-400" data-id="element-8813" data-mp="2phyhrof4fi6e">
            · {happeningNow.filter(h => h.live).length} active
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-8814" data-mp="2tis9ksvoph4k">
          {happeningNow.map((h, i) => <div key={i} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5" data-id="element-8815" data-mp="2yc3723elwljt" data-mpi={i}>
              <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8816" data-mp="33c9644n7habx">
                <img src={`https://cdn.simpleicons.org/${h.slug}/475569`} className="w-4.5 h-4.5 w-[18px] h-[18px] object-contain" alt="" onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
            }} data-id="element-8817" data-mp="3d93x82h5g8g6" />
                {h.live && <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-id="element-8818" data-mp="1mwo9oyihjro6">
                    <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 ${reducedMotion ? '' : 'animate-ping'}`} data-id="element-8819" data-mp="1fcdogyl532gc" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-8820" data-mp="1ajaw2ts3lz1q" />
                  </span>}
              </span>
              <div className="min-w-0" data-id="element-8821" data-mp="11m0hayg2nrs2">
                <p className="text-xs font-semibold text-neutral-900 truncate" data-id="element-8822" data-mp="11iw4jcojjnps">
                  {h.label}
                </p>
                <p className="text-[11px] text-neutral-400" data-id="element-8823" data-mp="1tbdeoaoavpem">
                  {h.live ? 'In progress' : 'Queued'}
                </p>
              </div>
            </div>)}
        </div>
      </motion.section>

      {/* ── Compact clickable stats ────────────────────────────────────────
                                         Supporting metrics, kept lean so they back up the hero rather than
                                         compete with it. Each routes to its real destination. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.16)} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-8824" data-mp="adgtrvngxg70">
        <button onClick={() => onNavigate('activity')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8825" data-mp="1dam4qegbafr">
          <div className="flex items-center gap-2 mb-3" data-id="element-8826" data-mp="3nrtwmc0b25gy">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center" data-id="element-8827" data-mp="3nqafykaqlsen">
              <CheckCircle2 size={15} className="text-emerald-600" data-id="element-8828" data-mp="3skwq6k531ex5" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8829" data-mp="3dvquh65ge6u0">
              Tasks done
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-8830" data-mp="34thngq9e8vld" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8831" data-mp="39rj4l5n9eku7">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8832" data-mp="2q60v3lcxiyse">14,231</span>
            <span className="text-xs font-semibold text-emerald-700" data-id="element-8833" data-mp="2l7fuhktrmd3f">+12%</span>
          </div>
        </button>

        <button onClick={() => onNavigate('pulses')} className="group text-left p-5 rounded-2xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all" data-id="element-8834" data-mp="2z7qjsjfffjmh">
          <div className="flex items-center gap-2 mb-3" data-id="element-8835" data-mp="3954z2fpm62d7">
            <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center" data-id="element-8836" data-mp="393k5asb4inge">
              <Activity size={15} className="text-neutral-600" data-id="element-8837" data-mp="3461sgqtzjo34" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8838" data-mp="3itwnwezdo6jw">
              Active pulses
            </span>
            <ArrowRight size={14} className="ml-auto text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all" data-id="element-8839" data-mp="3dyhcdtouemqn" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8840" data-mp="3my4bu9y0hih2">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8841" data-mp="1l7vs3i5qiq42">12</span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500" data-id="element-8842" data-mp="1l5jcvcrd2tup">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-8843" data-mp="1q4maic50hxsn" />
              smooth
            </span>
          </div>
        </button>

        <div className="group relative p-5 rounded-2xl border border-neutral-200 bg-white" data-id="element-8844" data-mp="1yzadfvn473ea">
          <div className="flex items-center gap-2 mb-3" data-id="element-8845" data-mp="129b7m8ycmize">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-8846" data-mp="12auoa0nx2w1p">
              <Clock size={15} className="text-amber-600" data-id="element-8847" data-mp="179ytyhbmubna" />
            </div>
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide" data-id="element-8848" data-mp="1b7wjjl9bezr7">
              Hours saved
            </span>
            <div className="relative ml-auto group/info" data-id="element-8849" data-mp="1g66im6y5uki3">
              <Info size={14} className="text-neutral-300 cursor-help hover:text-neutral-500 transition-colors" data-id="element-8850" data-mp="2mntmwehwhm7y" />
              <div className="absolute bottom-full right-0 mb-2 w-52 p-2.5 bg-neutral-900 text-white text-xs rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity pointer-events-none z-10 leading-relaxed" data-id="element-8851" data-mp="2rlv40tvrnbgs">
                Estimated based on average time per task type across all
                connected tools.
              </div>
            </div>
          </div>
          <div className="flex items-baseline gap-2" data-id="element-8852" data-mp="2nzgiew12tdr1">
            <span className="text-3xl font-bold text-neutral-900" data-id="element-8853" data-mp="2j0vfgoufwfyw">342</span>
            <span className="text-xs font-medium text-neutral-500" data-id="element-8854" data-mp="2iz279546hnos">
              ~14 days
            </span>
          </div>
        </div>
      </motion.section>

      {/* ── Tasks ───────────────────────────────────────────────────────────
                                         Work Giga teed up that needs the user's call — approvals, confirms,
                                         and reconnects. Complements Recent activity (what's done) with what
                                         still needs a human. Falls back to a calm empty state when clear. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.22)} className="mb-6" data-id="element-8855" data-mp="1wry5jwlktmn6">
        <div className="flex items-center justify-between mb-4" data-id="element-8856" data-mp="1nt2y6h6sq6ia">
          <div className="flex items-center gap-2.5" data-id="element-8857" data-mp="1rtmwho33vg60">
            <h2 className="text-base font-semibold text-neutral-900" data-id="element-8858" data-mp="1ru6awutjrmfa">Tasks</h2>
            {tasks.length > 0 && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-8859" data-mp="2fjqw7kd6avrq">
                {tasks.length}
              </span>}
          </div>
          <span className="text-xs font-medium text-neutral-400" data-id="element-8860" data-mp="26kng8p9aywhg">
            Needs your review
          </span>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-8861" data-mp="v3nkfjnqxzio">
          {tasks.length === 0 ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-8862" data-mp="102qpv57n27r6">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-8863" data-mp="rlgcmdwmqoyr">
                <ListChecks size={18} className="text-neutral-400" data-id="element-8864" data-mp="nitk0as4x00i" />
              </span>
              <div data-id="element-8865" data-mp="11jw78quv8gd0">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-8866" data-mp="11ib8ep1iqb9c">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-8867" data-mp="wigo54c90jx9">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-8868" data-mp="il1ol6sa6vrk">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-8869" data-mp="dubl5z3f12zi" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-8870" data-mp="3m93fpef707k2">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-8871" data-mp="2vs8un8dq6pey" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-8872" data-mp="3aogcw35kpgrl" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-8873" data-mp="3jpegikc3nm9n">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-8874" data-mp="3jo3cqkinmeij">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-8875" data-mp="3tf7shmg5m3ef">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-8876" data-mp="2ahih5y923g7">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-8877" data-mp="1hrukmpah15hz" />
                  </button>
                </div>)}
            </div>}
        </div>
      </motion.section>

      {/* ── Recent timeline ────────────────────────────────────────────────
                                         A calmer, more visual rail of the latest work across the product. */}
      <motion.section initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={ease(0.24)} data-id="element-8878" data-mp="25h6pxe2ogtf0">
        <div className="flex items-center justify-between mb-4" data-id="element-8879" data-mp="1vrn4q1rhsngz">
          <h2 className="text-base font-semibold text-neutral-900" data-id="element-8880" data-mp="1vrdf4fad0ibt">
            Recent activity
          </h2>
          <button onClick={() => onNavigate('activity')} className="group flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-8881" data-mp="2t942x8hv5rhp">
            View all
            <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" data-id="element-8882" data-mp="3q7akd2cahvuq" />
          </button>
        </div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white shadow-sm" data-id="element-8883" data-mp="308jasswxp7e">
          <div className="divide-y divide-neutral-100" data-id="element-8884" data-mp="3u71ffi8umy52">
            {recentHeartbeat.map((item, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors group/item cursor-pointer" data-id="element-8885" data-mp="cyo5qigzwaak" data-mpi={i}>
                <div className="relative flex items-center justify-center shrink-0 mt-0.5" data-id="element-8886" data-mp="7yi9sq3p01mo">
                  <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm transition-transform group-hover/item:scale-105" data-id="element-8887" data-mp="lpwvj4ozigxf">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                  ;
                  (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
                }} data-id="element-8888" data-mp="vomgj6c6ghhw" />
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-8889" data-mp="1fb7kf8j1go4d">
                    {item.status === 'running' ? <Loader2 size={9} className={`text-white ${reducedMotion ? '' : 'animate-spin'}`} data-id="element-8890" data-mp="15ax59idg16mk" /> : <Check size={9} className="text-white" data-id="element-8891" data-mp="rjsc8z4kzcl7" />}
                  </span>
                </div>

                <div className="flex-1 min-w-0" data-id="element-8892" data-mp="wdeeryh1603a">
                  <div className="flex items-start justify-between gap-3 mb-1.5" data-id="element-8893" data-mp="hnpbnxpdnm0d">
                    <span className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-8894" data-mp="mma1d3706nak">
                      {item.task}
                    </span>
                    <span className="text-xs font-medium text-neutral-400 shrink-0 mt-0.5" data-id="element-8895" data-mp="8nsjvn7oy713">
                      {item.time}
                    </span>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-8896" data-mp="2fb17cj9chq21">
                    {item.type}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </motion.section>

      {/* Mobile CTA */}
      <button onClick={onStartTask} className="sm:hidden mt-6 w-full flex items-center justify-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8897" data-mp="20noh4h8lew5b">
        <Sparkles size={16} data-id="element-8898" data-mp="2obfu7n1yjd7n" />
        Start a task
      </button>
    </div>;
}
// ── Direction #2: Progressive disclosure based on state ──────────────────────
// First-ever load → a guided "get started" state. Once set up → the calm
// status overview. A sub-toggle lets you preview both moments.
function ProgressiveOverview({
  onConnect,
  onStartTask,
  onNavigate,
  state,
  onStateChange
}: {
  onConnect: () => void;
  onStartTask: () => void;
  onNavigate: (nav: string) => void; // first/returning is lifted to the parent Dashboard so the right chat panel
  // can stay hidden during first-time setup.
  state: 'first' | 'returning';
  onStateChange: (s: 'first' | 'returning') => void;
}) {
  const reducedMotion = useReducedMotion();
  const setState = onStateChange;
  // Track which step the user is currently on (1, 2, or 3)
  const [currentStep, setCurrentStep] = useState(1);
  // Set once all three steps are finished — shows the celebratory "all set"
  // payoff before the user drops into the everyday (returning) overview.
  const [allSet, setAllSet] = useState(false);
  const steps = [{
    id: 1,
    icon: Plug,
    title: 'Connect your tools',
    detail: 'Giga needs access to your apps to do work on your behalf.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8899" data-mp="2hwhbblodgyrv">
          <div className="flex flex-wrap gap-2" data-id="element-8900" data-mp="2muaes6yajeog">
            {['slack', 'gmail', 'notion', 'linear', 'salesforce'].map(tool => <div key={tool} className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-8901" data-mp="1rjkohcwq48l7">
                  <img src={`https://cdn.simpleicons.org/${tool}/475569`} className="w-5 h-5 object-contain" alt={tool} onError={e => {
            ;
            (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
          }} data-id="element-8902" data-mp="1mkrcjox34pn5" />
                </div>)}
            <div className="w-10 h-10 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center border-dashed" data-id="element-8903" data-mp="18ue65qzudizx">
              <span className="text-neutral-400 text-xs font-medium" data-id="element-8904" data-mp="13sos02hbop7m">+20</span>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8905" data-mp="1dspf7zibbph3">
            <button onClick={() => {
          onConnect();
          // Simulate completing the step after a short delay
          setTimeout(() => setCurrentStep(2), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8906" data-mp="1dr4ji6k9i0hn">
              Connect integrations
            </button>
            <button onClick={() => setCurrentStep(2)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8907" data-mp="31idfru0rik02">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 2,
    icon: Sparkles,
    title: 'Try your first task',
    detail: 'See Giga in action. Ask it to summarize a thread or draft an email.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8908" data-mp="3kgpqvlb8i3ya">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm" data-id="element-8909" data-mp="3kgfzpuegdqr0">
            <p className="text-sm text-neutral-600 mb-3 font-medium" data-id="element-8910" data-mp="36gez8g23olc2">
              Try asking:
            </p>
            <div className="flex flex-col gap-2" data-id="element-8911" data-mp="3ah7b7h2cx4c1">
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-8912" data-mp="1v1i7gyej7i10">
                "Summarize my unread emails from today"
              </button>
              <button onClick={() => {
            onStartTask();
            setTimeout(() => setCurrentStep(3), 500);
          }} className="text-left px-3 py-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors border border-transparent hover:border-neutral-200" data-id="element-8913" data-mp="3s0h4axcn9l5t">
                "What are my open tasks in Linear?"
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8914" data-mp="39136g4bsnqa1">
            <button onClick={() => {
          onStartTask();
          setTimeout(() => setCurrentStep(3), 500);
        }} className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8915" data-mp="391v1ne1hblzs">
              Start a new chat
            </button>
            <button onClick={() => setCurrentStep(3)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8916" data-mp="12byf30ryaoh6">
              Skip for now
            </button>
          </div>
        </div>
  }, {
    id: 3,
    icon: Activity,
    title: 'Set up a recurring pulse',
    detail: 'Automate your routine. Have Giga send a daily brief or weekly report.',
    content: <div className="mt-4 flex flex-col gap-4" data-id="element-8917" data-mp="1oj2chx3is3cz">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200" data-id="element-8918" data-mp="rttf0wppsyg5">
            <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-8919" data-mp="vu4xc2ulumog">
              <Clock size={18} className="text-[#EA580C]" data-id="element-8920" data-mp="10soku7ouk6ix" />
            </div>
            <div data-id="element-8921" data-mp="15u49sau5ax6l">
              <p className="text-sm font-semibold text-neutral-900" data-id="element-8922" data-mp="15unmnd4xuvdr">
                Daily Morning Briefing
              </p>
              <p className="text-xs text-neutral-500" data-id="element-8923" data-mp="2cbiw4c2wo3ee">
                Runs every weekday at 8:00 AM
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-8924" data-mp="32dbr8kvnrfcu">
            <button onClick={() => setAllSet(true)} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm" data-id="element-8925" data-mp="2yaowab3oxebf">
              Complete setup
            </button>
            <button onClick={() => setAllSet(true)} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors" data-id="element-8926" data-mp="37b4wnbp505c2">
              Skip for now
            </button>
          </div>
        </div>
  }];
  const progressPercentage = (currentStep - 1) / steps.length * 100;
  return <div className="max-w-3xl mx-auto py-6" data-id="element-8927" data-mp="3v1hcr9ujd8dq">
      <div className="flex justify-center mb-8" data-id="element-8928" data-mp="3v068za13c0mm">
        <div className="inline-flex p-1 rounded-full bg-neutral-100 border border-neutral-200" data-id="element-8929" data-mp="3q1cxfn5d6jp3">
          {(['first', 'returning'] as const).map(s => <button key={s} onClick={() => setState(s)} aria-pressed={state === s} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${state === s ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-8930" data-mp="20ngdinv2qjd4">
              {s === 'first' ? 'First-time' : 'Returning'}
            </button>)}
        </div>
      </div>

      {state === 'returning' ? <CommandCenterOverview onStartTask={onStartTask} onNavigate={onNavigate} data-id="element-8931" data-mp="2czhpj5mx4gq" /> : allSet ? <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="flex flex-col items-center text-center py-10" data-id="element-8932" data-mp="6e18rcpeea7m">
          <motion.span initial={reducedMotion ? false : {
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 18,
        delay: reducedMotion ? 0 : 0.05
      }} className="w-16 h-16 rounded-2xl bg-[#0F9D58] text-white flex items-center justify-center mb-6 shadow-sm" data-id="element-8933" data-mp="3fksspax9ogmz">
            <CheckCircle2 size={32} data-id="element-8934" data-mp="20vddpcc0dmx" />
          </motion.span>
          <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-2" data-id="element-8935" data-mp="1zaj80txiwpl">
            You're all set, Faith
          </h1>
          <p className="text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-8936" data-mp="3o8v99o1zrs2d">
            Giga is connected and ready. Ask it to get something done — or let
            it keep working in the background.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3" data-id="element-8937" data-mp="kso7xo71u6wr">
            <button onClick={onStartTask} className="inline-flex items-center gap-2 bg-[#2B211A] hover:bg-[#1A1410] text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-id="element-8938" data-mp="pqg26ov62544">
              <Sparkles size={16} data-id="element-8939" data-mp="fs0j996am25p" />
              Start a task
            </button>
            <button onClick={() => setState('returning')} className="px-6 py-3 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-8940" data-mp="2vjo3q544e27m">
              Go to dashboard
            </button>
          </div>
        </motion.div> : <motion.div initial={reducedMotion ? false : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: reducedMotion ? 0 : 0.45,
      ease: 'easeOut'
    }} data-id="element-8941" data-mp="1ijulj6pugwfd">
          <div className="mb-10" data-id="element-8942" data-mp="1rjq2js62zlnb">
            <p className="text-sm font-medium text-[#EA580C] mb-1.5" data-id="element-8943" data-mp="1nkievz1jppcr">
              Welcome, Faith
            </p>
            <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-8944" data-mp="ztv3h3sf8hlg">
              Let's get Giga working for you
            </h1>
            <p className="text-lg text-neutral-500 leading-relaxed max-w-xl" data-id="element-8945" data-mp="utgspdzfqtjq">
              Setup takes about 2 minutes. Once you&rsquo;re connected,
              here&rsquo;s what you can put Giga to work on:
            </p>

            {/* What you can set up — concrete product surfaces, not a pitch */}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-8946" data-mp="g4au9s8f97cw">
              {[{
            icon: MessageSquare,
            label: 'Ask in chat'
          }, {
            icon: LayoutTemplate,
            label: 'Run recipes'
          }, {
            icon: Activity,
            label: 'Schedule pulses'
          }, {
            icon: Map,
            label: 'Track journeys'
          }, {
            icon: UserCircle,
            label: 'Build assistants'
          }].map(c => <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700" data-id="element-8947" data-mp="2py6p83xhynbs">
                  <c.icon size={14} className="text-[#EA580C]" data-id="element-8948" data-mp="1xb3snhcdsmo6" />
                  {c.label}
                </span>)}
            </div>

            {/* Progress Bar */}
            <div className="mt-8 flex items-center gap-4" data-id="element-8949" data-mp="277ysq4oayv4k">
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden" data-id="element-8950" data-mp="279jo1wifyi3h">
                <div className="h-full bg-[#EA580C] transition-all duration-500 ease-out rounded-full" style={{
              width: `${progressPercentage}%`
            }} data-id="element-8951" data-mp="3dnsm2kqecqgm" />
              </div>
              <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider" data-id="element-8952" data-mp="16yqqs62e8h63">
                {currentStep - 1} of {steps.length} done
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-8953" data-mp="x8nyk833j78t">
            {steps.map(step => {
          const isPast = step.id < currentStep;
          const isActive = step.id === currentStep;
          const isFuture = step.id > currentStep;
          return <div key={step.id} className={`relative rounded-2xl border transition-all duration-300 overflow-hidden ${isActive ? 'border-[#EA580C] bg-white shadow-md ring-1 ring-[#EA580C]/10' : isPast ? 'border-neutral-200 bg-neutral-50/50' : 'border-neutral-200 bg-white opacity-60'}`} data-id="element-8954" data-mp="jah5r6d07xg0">
                  <div className={`px-6 py-5 flex items-start gap-4 ${isActive ? '' : 'items-center'}`} data-id="element-8955" data-mp="9emks4lb5qa2">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isPast ? 'bg-[#0F9D58] text-white' : isActive ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-8956" data-mp="23qb1ab76u0wk">
                      {isPast ? <CheckCircle2 size={24} data-id="element-8957" data-mp="15d6w752ogt13" /> : <step.icon size={24} data-id="element-8958" data-mp="15cduiiikstpt" />}
                    </div>

                    <div className="flex-1 min-w-0 pt-1" data-id="element-8959" data-mp="rdxeg0ytpqv0">
                      <div className="flex items-center justify-between gap-4" data-id="element-8960" data-mp="wdjsgcmh7b4v">
                        <h3 className={`text-base font-semibold ${isPast ? 'text-neutral-500 line-through decoration-neutral-300' : 'text-neutral-900'}`} data-id="element-8961" data-mp="wfmopee820i1">
                          {step.title}
                        </h3>
                        {isPast && <button onClick={() => setCurrentStep(step.id)} className="text-xs font-medium text-neutral-400 hover:text-neutral-600 transition-colors" data-id="element-8962" data-mp="8p0kjy7c90db">
                            Revisit
                          </button>}
                      </div>

                      {(!isPast || isActive) && <p className={`text-sm mt-1 ${isActive ? 'text-neutral-600' : 'text-neutral-400'}`} data-id="element-8963" data-mp="3pnvr7wwpj83">
                          {step.detail}
                        </p>}

                      {/* Expandable Content for Active Step */}
                      <div className={`grid transition-all duration-300 ease-in-out ${isActive ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-8964" data-mp="3c8pyzvbcux5b">
                        <div className="overflow-hidden" data-id="element-8965" data-mp="3l7k0o03jmdpq">{step.content}</div>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {/* Global escape hatch — never trap a new user in the flow */}
          <div className="mt-6 text-center" data-id="element-8966" data-mp="cs0a58u9b9ed">
            <button onClick={() => setState('returning')} className="text-sm font-medium text-neutral-400 hover:text-neutral-700 transition-colors" data-id="element-8967" data-mp="cqozcp2e91dr">
              Skip setup — I'll explore on my own
            </button>
          </div>
        </motion.div>}
    </div>;
}
// ── Direction #3: Chat-first, everything else collapses ──────────────────────
// Leans fully into chat as the one surface: a big composer takes center stage,
// stats demote to a slim chip row beneath it.
function ChatFirstOverview({
  onStartTask
}: {
  onStartTask: () => void;
}) {
  return <div className="flex flex-col h-full min-h-[480px]" data-id="element-8968" data-mp="ysquevn0n4oo">
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-id="element-8969" data-mp="yr62zew001v1">
        <span className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center mb-6" data-id="element-8970" data-mp="13ks3k8ow0fah">
          <Sparkles size={26} className="text-white" data-id="element-8971" data-mp="18hr00xw0wybu" />
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 tracking-tight mb-3" data-id="element-8972" data-mp="1cj40stk9ebbw">
          What can I help you with?
        </h1>
        <p className="text-neutral-500 mb-8 max-w-md" data-id="element-8973" data-mp="1hgvv1u8dm9j9">
          Describe a task and Giga will get it done across your connected tools.
        </p>
        <div className="w-full max-w-2xl" data-id="element-8974" data-mp="3jb4wc36jmy1n">
          <div className="relative rounded-2xl border border-neutral-200 bg-white shadow-sm focus-within:border-[#EA580C] focus-within:ring-1 focus-within:ring-[#EA580C] transition-colors" data-id="element-8975" data-mp="3jc6684btpehs">
            <textarea placeholder="Ask Giga to draft, summarize, update, or automate…" rows={3} className="w-full bg-transparent rounded-2xl px-5 py-4 pr-14 text-sm resize-none focus:outline-none" data-id="element-8976" data-mp="3grgkqh1h11nq" />
            <button onClick={onStartTask} aria-label="Send" className="absolute right-3 bottom-3 w-9 h-9 rounded-xl bg-[#2B211A] hover:bg-[#1A1410] text-white flex items-center justify-center transition-colors" data-id="element-8977" data-mp="370lw560r5pz4">
              <Send size={16} data-id="element-8978" data-mp="3havam3ahx96" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 pt-8" data-id="element-8979" data-mp="3uqg9doozhgjw">
        {OVERVIEW_STATS.map(s => <span key={s.label} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs" data-id="element-8980" data-mp="3por0aelbncyg">
            <span className="font-bold text-neutral-900" data-id="element-8981" data-mp="2eby9p749oujz">{s.value}</span>
            <span className="text-neutral-500" data-id="element-8982" data-mp="2e92cxm45obx4">{s.label}</span>
          </span>)}
      </div>
    </div>;
}
// ── Create-a-pulse slide-over panel ──────────────────────────────────────────
// A right-anchored sheet for composing a new recurring pulse: name + prompt,
// a "Saved time" vs "Custom" run mode, a list of selectable saved times, a
// destination note, and an advanced-settings disclosure. Slides in from the
// right over a dimmed backdrop, mirroring the product's real pulse builder.
type SavedTime = {
  id: string;
  label: string;
  cadence: string;
};
const PULSE_SAVED_TIMES: SavedTime[] = [{
  id: 'morning',
  label: 'Morning',
  cadence: 'Daily at 08:00 Etc/UTC'
}, {
  id: 'afternoon',
  label: 'Afternoon',
  cadence: 'Daily at 13:00 Etc/UTC'
}, {
  id: 'evening',
  label: 'Evening',
  cadence: 'Daily at 18:00 Etc/UTC'
}, {
  id: 'weekly',
  label: 'Weekly',
  cadence: 'Mondays at 09:00 Etc/UTC'
}, {
  id: 'monthly',
  label: 'Monthly',
  cadence: 'Monthly (day 1) at 09:00 Etc/UTC'
}];
function PulseCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [runMode, setRunMode] = useState<'saved' | 'custom'>('saved');
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const toggleTime = (id: string) => setSelectedTimes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  return <AnimatePresence data-id="element-8983" data-mp="1g0i0ov0okqc3">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-8984" data-mp="2wg1drnpvyaww">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-8985" data-mp="2whw2ze86wp6s" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a pulse" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-8986" data-mp="316j3ahksa4km">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-8987" data-mp="3f3zavwetg4dy">
              <div data-id="element-8988" data-mp="3b3fif656toy5">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-8989" data-mp="3b1uo9hmsc80t">
                  New pulse
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-8990" data-mp="6ofpfjkdzq9g">
                  Create a pulse
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-8991" data-mp="1pch5jveg4sup">
                <X size={20} data-id="element-8992" data-mp="1ybca731uduvw" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-8993" data-mp="23b71v8tfyoi0">
              {/* Name */}
              <div data-id="element-8994" data-mp="28atebg1g40pr">
                <label htmlFor="pulse-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-8995" data-mp="289ie1q77lb3e">
                  Name
                </label>
                <input id="pulse-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Morning briefing" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-8996" data-mp="2i03ap83bo5jo" />
              </div>

              {/* Prompt */}
              <div data-id="element-8997" data-mp="vxcuf88gbbti">
                <label htmlFor="pulse-prompt" className="block text-sm font-semibold text-neutral-900" data-id="element-8998" data-mp="vwtokpw2sdvu">
                  Prompt
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-8999" data-mp="r034zsl2b05o">
                  What the assistant should do when this pulse runs.
                </p>
                <textarea id="pulse-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Summarise my unread messages and flag anything urgent…" rows={5} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-9000" data-mp="10w6c7curtozh" />
              </div>

              {/* When should it run? */}
              <div data-id="element-9001" data-mp="1jl4i17zfzba3">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3" data-id="element-9002" data-mp="3qd59o6ilvduk">
                  When should it run?
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-9003" data-mp="3lfc33cde4htr">
                  <button type="button" onClick={() => setRunMode('saved')} aria-pressed={runMode === 'saved'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'saved' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-9004" data-mp="20w9tsoh2hm96">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'saved' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-9005" data-mp="1w51ghhcm9gqq">
                      <Clock size={18} data-id="element-9006" data-mp="1n4lhak2wovro" />
                    </span>
                    <span className="min-w-0" data-id="element-9007" data-mp="1n2s6csl9fpdv">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-9008" data-mp="188dwbhjobqbc">
                        Saved time
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-9009" data-mp="1d8rv08zdxnwp">
                        Runs with your reusable times.
                      </span>
                    </span>
                    {runMode === 'saved' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-9010" data-mp="17igw6subkrca">
                        <CheckCircle2 size={14} data-id="element-9011" data-mp="13i6mp67bjiso" />
                      </span>}
                  </button>
                  <button type="button" onClick={() => setRunMode('custom')} aria-pressed={runMode === 'custom'} className={`relative flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${runMode === 'custom' ? 'border-neutral-900 bg-white' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-9012" data-mp="ygherzfe7l8t">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${runMode === 'custom' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`} data-id="element-9013" data-mp="jrl743h90a8q">
                      <Settings size={18} data-id="element-9014" data-mp="atr8umzxb6ix" />
                    </span>
                    <span className="min-w-0" data-id="element-9015" data-mp="5s1y78gm4uvd">
                      <span className="block text-sm font-semibold text-neutral-900" data-id="element-9016" data-mp="5v7qsxogaevu">
                        Custom
                      </span>
                      <span className="block text-xs text-neutral-500 leading-snug mt-0.5" data-id="element-9017" data-mp="3mw94uyd4170p">
                        Its own schedule.
                      </span>
                    </span>
                    {runMode === 'custom' && <span className="absolute top-3 right-3 flex items-center justify-center w-5 h-5 rounded-full bg-neutral-900 text-white" data-id="element-9018" data-mp="pko1vmcimro">
                        <CheckCircle2 size={14} data-id="element-9019" data-mp="5kzzkgwvs6kx" />
                      </span>}
                  </button>
                </div>
              </div>

              {/* Which times? (only for saved-time mode) */}
              {runMode === 'saved' && <div data-id="element-9020" data-mp="3e2mg3s7bl410">
                  <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9021" data-mp="3e2e03rfwhill">
                    Which times?
                  </h3>
                  <p className="text-xs text-neutral-400 mt-0.5 mb-3" data-id="element-9022" data-mp="3ivyznnx56kmw">
                    Pick the saved time(s) this runs with. Manage them in Pulse
                    Settings.
                  </p>
                  <div className="space-y-2.5" data-id="element-9023" data-mp="2qbgwr3267tly">
                    {PULSE_SAVED_TIMES.map(t => {
                const selected = selectedTimes.includes(t.id);
                return <button key={t.id} type="button" onClick={() => toggleTime(t.id)} aria-pressed={selected} className={`w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-colors ${selected ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'}`} data-id="element-9024" data-mp="2dd11a55c8zl4">
                          <span className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${selected ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-400'}`} data-id="element-9025" data-mp="1zejhgiik078h">
                            <Clock size={18} data-id="element-9026" data-mp="29cyx9pc4ru2o" />
                          </span>
                          <span className="min-w-0 flex-1" data-id="element-9027" data-mp="24curat8umhqp">
                            <span className="block text-sm font-bold text-neutral-900" data-id="element-9028" data-mp="24fgyusvqox8x">
                              {t.label}
                            </span>
                            <span className="block text-sm text-neutral-500 truncate" data-id="element-9029" data-mp="3frrjgc0bxn2a">
                              {t.cadence}
                            </span>
                          </span>
                          {selected && <CheckCircle2 size={18} className="text-neutral-900 shrink-0" data-id="element-9030" data-mp="3sc6ni8m23j3i" />}
                        </button>;
              })}
                  </div>
                </div>}

              {/* Destination */}
              <div data-id="element-9031" data-mp="3ies90ejp14du">
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9032" data-mp="9ww5zpnf4hwt">
                  Destination
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed mt-1" data-id="element-9033" data-mp="ew8nrvzfmysj">
                  Sent via the destination set on the saved time(s) above —
                  manage it in Pulse Settings.
                </p>
              </div>

              {/* Advanced settings */}
              <div className="border-t border-neutral-100 pt-5" data-id="element-9034" data-mp="sw07mdcmr9eg">
                <button type="button" onClick={() => setAdvancedOpen(v => !v)} aria-expanded={advancedOpen} className="flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-neutral-600 transition-colors" data-id="element-9035" data-mp="stnh3bst3qps">
                  <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-0' : '-rotate-90'}`} data-id="element-9036" data-mp="453zl0vkpbcp" />
                  Advanced settings
                </button>
                <AnimatePresence initial={false} data-id="element-9037" data-mp="3v92gqeb9lroc">
                  {advancedOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-9038" data-mp="3qdvk1qgexn8x">
                      <div className="pt-4 space-y-3 text-sm text-neutral-500" data-id="element-9039" data-mp="cy7c96ye9k6a">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-9040" data-mp="32mhsvntzzxpj">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-9041" data-mp="2xnmxzniraip2" />
                          Skip runs when there's nothing to report
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-9042" data-mp="1i45r5k0tadly">
                          <input type="checkbox" className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900" data-id="element-9043" data-mp="1n41xvr0w8j57" />
                          Include links to the source items
                        </label>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-9044" data-mp="20x1e5v8i2hdt">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-9045" data-mp="25sgr8kyoo996">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-9046" data-mp="bid4i1randjh">
                Create pulse
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Create-a-journey slide-over panel ────────────────────────────────────────
// A right-anchored sheet for spinning up a new journey: name, space, status,
// an optional overview that seeds journey.md, and a check-in cadence. Slides in
// from the right over a dimmed backdrop, mirroring the pulse builder so the two
// creation flows feel like one family.
const JOURNEY_SPACES = ['Everyone', 'Giga Main', 'Marketing', 'Product', 'Sales'];
const JOURNEY_STATUSES = ['Unset', 'In focus', 'Active', 'Paused', 'Done'];
const JOURNEY_CADENCES = ['No check-ins', 'Daily', 'Weekly', 'Every 2 weeks', 'Monthly'];
function JourneyField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return <div className="relative" data-id="element-9047" data-mp="2z1jrqorym6qx">
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium text-neutral-900 bg-white focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] cursor-pointer" data-id="element-9048" data-mp="2z4xtbussubxb">
        {options.map(o => <option key={o} value={o} data-id="element-9049" data-mp="8r4z3fiys014">
            {o}
          </option>)}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-9050" data-mp="4p052folt1dy" />
    </div>;
}
function JourneyCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [status, setStatus] = useState('Unset');
  const [overview, setOverview] = useState('');
  const [cadence, setCadence] = useState('No check-ins');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-9051" data-mp="34ouhkik54u13">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-9052" data-mp="34lq2ur91ufw6">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-9053" data-mp="39n5p2mmyqsg5" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create a journey" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-9054" data-mp="3ob8tvsp9ry3d">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-9055" data-mp="29giqjhh032hj">
              <div data-id="element-9056" data-mp="2jclqcgodqp1b">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-9057" data-mp="2jd58nyhxzffv">
                  New journey
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-9058" data-mp="2ebpl9zsakwub">
                  Create a journey
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-9059" data-mp="1lpwivrawrk82">
                <X size={20} data-id="element-9060" data-mp="1vnkojca1yc50" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-9061" data-mp="17xgiyzdb02bd">
              {/* Name */}
              <div data-id="element-9062" data-mp="12z5c8xib27xd">
                <label htmlFor="journey-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-9063" data-mp="12xki38zwkr01">
                  Name
                </label>
                <input id="journey-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q3 Launch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-9064" data-mp="a8ttnchm1p0z" />
              </div>

              {/* Space */}
              <div data-id="element-9065" data-mp="ozudserpz7cy">
                <label className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-9066" data-mp="twt40m89iu5v">
                  Space
                </label>
                <JourneyField label="Space" value={space} onChange={setSpace} options={JOURNEY_SPACES} data-id="element-9067" data-mp="xy692u3j6tbq" />
              </div>

              {/* Status */}
              <div data-id="element-9068" data-mp="12v53ldr3x2ag">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-9069" data-mp="2zq19gdkc7ben">
                  Status
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-9070" data-mp="34pxca9haswsm">
                  How active this journey currently is.
                </p>
                <JourneyField label="Status" value={status} onChange={setStatus} options={JOURNEY_STATUSES} data-id="element-9071" data-mp="2x76a27wzswq9" />
              </div>

              {/* Overview */}
              <div data-id="element-9072" data-mp="2ngbnt3jqxx4t">
                <label htmlFor="journey-overview" className="block text-sm font-semibold text-neutral-900" data-id="element-9073" data-mp="2nftg9armvwhl">
                  Overview
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-9074" data-mp="3g4h8jqasvm4n">
                  Optional — seeds the journey's journey.md. You can edit it
                  later.
                </p>
                <textarea id="journey-overview" value={overview} onChange={e => setOverview(e.target.value)} placeholder="What this journey is about, goals, context…" rows={6} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-9075" data-mp="365i4ym4w6goi" />
              </div>

              {/* Check-in cadence */}
              <div className="border-t border-neutral-100 pt-6" data-id="element-9076" data-mp="1s77ygjvmjtra">
                <label className="block text-sm font-semibold text-neutral-900" data-id="element-9077" data-mp="1x4hxmr8tfjsb">
                  Check-in cadence
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2 leading-relaxed" data-id="element-9078" data-mp="1iad48d6dwfit">
                  How often your assistant should proactively check in on this
                  journey. Leave as “No check-ins” to keep it passive.
                </p>
                <JourneyField label="Check-in cadence" value={cadence} onChange={setCadence} options={JOURNEY_CADENCES} data-id="element-9079" data-mp="1e6xbvt1cl1bx" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-9080" data-mp="14gcdo6pl5ytj">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-9081" data-mp="qgui98hsbnb7">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-9082" data-mp="btvt5k6eh4zn">
                Create journey
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function AssistantCreatePanel({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [name, setName] = useState('');
  const [space, setSpace] = useState('Everyone');
  const [description, setDescription] = useState('');
  const [identity, setIdentity] = useState('');
  // Close on Escape while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-9083" data-mp="18m7u6lpxpw9j">
      {open && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-9084" data-mp="2fx3zn1715mro">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-neutral-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} aria-hidden="true" data-id="element-9085" data-mp="2fzgeh5hhrh0i" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Create an assistant" className="relative h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col" initial={{
        x: reduceMotion ? 0 : '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: reduceMotion ? 0 : '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 38
      }} data-id="element-9086" data-mp="2tusjq6t9wpvo">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-neutral-100" data-id="element-9087" data-mp="1diwsdr39unf1">
              <div data-id="element-9088" data-mp="1igy8c35ei6ma">
                <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-neutral-400" data-id="element-9089" data-mp="1igezdbxqaske">
                  New assistant
                </p>
                <h2 className="text-2xl font-bold text-neutral-900 mt-1" data-id="element-9090" data-mp="15zdnxvuv5rdf">
                  Create an assistant
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-neutral-400 hover:text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors" data-id="element-9091" data-mp="10yg65kdrmzcg">
                <X size={20} data-id="element-9092" data-mp="rzkz662wdl83" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7" data-id="element-9093" data-mp="n7l5uwhc1qkh">
              {/* Name */}
              <div data-id="element-9094" data-mp="iakzekgkmlsu">
                <label htmlFor="assistant-name" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-9095" data-mp="ia1k7bib2bii">
                  Name
                </label>
                <input id="assistant-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Glitch" className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C]" data-id="element-9096" data-mp="8bvjuyc5ddfr" />
              </div>

              {/* Space */}
              <div data-id="element-9097" data-mp="29gfqy2mev67e">
                <label htmlFor="assistant-space" className="block text-sm font-semibold text-neutral-900 mb-2" data-id="element-9098" data-mp="29gpg5nzmt9c1">
                  Space
                </label>
                <div className="relative" data-id="element-9099" data-mp="25czub6acd5up">
                  <select id="assistant-space" value={space} onChange={e => setSpace(e.target.value)} className="w-full appearance-none border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] bg-white" data-id="element-9100" data-mp="1hognywaur359">
                    <option value="Everyone" data-id="element-9101" data-mp="1lqlazc6sp1ix">Everyone</option>
                    <option value="Private" data-id="element-9102" data-mp="1lp0gfmkhdil2">Private</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-neutral-400" data-id="element-9103" data-mp="xz64d77zjy1v">
                    <ChevronDown size={16} data-id="element-9104" data-mp="jvqn337zh2bd" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-9105" data-mp="on8sc3h1hp25">
                <label htmlFor="assistant-description" className="block text-sm font-semibold text-neutral-900" data-id="element-9106" data-mp="onrzqq92cv1x">
                  Description
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-9107" data-mp="exp7wtdohn56">
                  Short summary shown on the assistants list.
                </p>
                <textarea id="assistant-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="What this assistant does" rows={3} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-9108" data-mp="12kduhsdmu3s4" />
              </div>

              {/* Identity */}
              <div data-id="element-9109" data-mp="3idvzw2mqdvy4">
                <label htmlFor="assistant-identity" className="block text-sm font-semibold text-neutral-900" data-id="element-9110" data-mp="3ieff3bkzy68g">
                  Identity
                </label>
                <p className="text-xs text-neutral-400 mt-0.5 mb-2" data-id="element-9111" data-mp="1jvkjd4k6jglp">
                  The assistant's system prompt — who it is and how it behaves.
                  You can refine this later.
                </p>
                <textarea id="assistant-identity" value={identity} onChange={e => setIdentity(e.target.value)} placeholder="Describe who this assistant is and how it should behave..." rows={8} className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] resize-none" data-id="element-9112" data-mp="1a69bfl914hze" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-neutral-100" data-id="element-9113" data-mp="r9825o8fzvpn">
              <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 px-4 py-2.5 transition-colors" data-id="element-9114" data-mp="2qhmx6dqhatvg">
                Cancel
              </button>
              <button onClick={onClose} className="text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 px-5 py-2.5 rounded-xl transition-colors" data-id="element-9115" data-mp="2t2cjg38nnaqk">
                Create assistant
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
// ── Conversations view ───────────────────────────────────────────────────────
type ConvOwner = 'user' | 'assistant';
type ConvScope = 'private' | 'shared';
type ConvSource = 'cli' | 'web' | 'slack';
type ConversationItem = {
  title: string;
  preview: string;
  time: string;
  owner: ConvOwner;
  scope: ConvScope;
  source: ConvSource;
  // Author of the conversation — drives the avatar filter row.
  authorId: string;
};
const CONV_AUTHORS: {
  id: string;
  name: string;
  initials: string;
  color: string;
}[] = [{
  id: 'nt',
  name: 'Nina Torres',
  initials: 'NT',
  color: 'bg-[#1A1410] text-white'
}, {
  id: 'nc',
  name: 'Noah Cole',
  initials: 'NC',
  color: 'bg-indigo-600 text-white'
}, {
  id: 'no',
  name: 'Nadia Owens',
  initials: 'NO',
  color: 'bg-rose-700 text-white'
}, {
  id: 'ev',
  name: 'Evan Vargas',
  initials: 'Ev',
  color: 'bg-rose-800 text-white'
}, {
  id: 'gm',
  name: 'Grace Mwangi',
  initials: 'GM',
  color: 'bg-sky-700 text-white'
}, {
  id: 'gg',
  name: 'Giga (Assistant)',
  initials: 'GG',
  color: 'bg-neutral-900 text-white'
}];
const CONVERSATIONS: ConversationItem[] = [{
  title: 'Q3 renewal strategy',
  preview: 'Here is the draft brief for the Northwind renewal…',
  time: '10 mins ago',
  owner: 'user',
  scope: 'private',
  source: 'web',
  authorId: 'nt'
}, {
  title: 'Weekly standup recap',
  preview: 'Summarized the engineering sync and posted to Slack.',
  time: '1 hour ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}, {
  title: 'Ad spend audit results',
  preview: 'Paused 3 ad sets under 0.8× ROAS and exported a PDF.',
  time: 'Yesterday',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'nc'
}, {
  title: 'Support ticket triage',
  preview: 'Replied to 6 tickets and escalated #4092 to eng.',
  time: 'Yesterday',
  owner: 'assistant',
  scope: 'private',
  source: 'cli',
  authorId: 'gg'
}, {
  title: 'Pipeline cleanup',
  preview: 'Updated 14 stale deals and flagged 2 at risk.',
  time: '2 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'no'
}, {
  title: 'Onboarding doc for Acme',
  preview: 'Created the Notion onboarding doc and invited the client.',
  time: '3 days ago',
  owner: 'user',
  scope: 'shared',
  source: 'web',
  authorId: 'ev'
}, {
  title: 'Deploy hotfix to staging',
  preview: 'Ran the release script and confirmed the build is green.',
  time: '3 days ago',
  owner: 'user',
  scope: 'private',
  source: 'cli',
  authorId: 'gm'
}, {
  title: 'Customer NPS digest',
  preview: 'Pulled this week’s responses and flagged 2 detractors.',
  time: '4 days ago',
  owner: 'assistant',
  scope: 'shared',
  source: 'slack',
  authorId: 'gg'
}];
function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel
}: {
  options: {
    value: T;
    label: string;
    icon?: React.ReactNode;
  }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return <div role="tablist" aria-label={ariaLabel} className="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-neutral-200 bg-neutral-50" data-id="element-9116" data-mp="3jx5zwk98coam">
      {options.map(opt => {
      const selected = opt.value === value;
      return <button key={opt.value} role="tab" aria-selected={selected} onClick={() => onChange(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'}`} data-id="element-9117" data-mp="gd1logiaz7tk">
            {opt.icon}
            {opt.label}
          </button>;
    })}
    </div>;
}
function ConversationsView() {
  const [owner, setOwner] = useState<ConvOwner>('user');
  const [scope, setScope] = useState<ConvScope>('private');
  const [source, setSource] = useState<'all' | ConvSource>('all');
  const [authorId, setAuthorId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const filtered = CONVERSATIONS.filter(c => {
    if (c.owner !== owner) return false;
    if (c.scope !== scope) return false;
    if (source !== 'all' && c.source !== source) return false;
    if (authorId && c.authorId !== authorId) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!c.title.toLowerCase().includes(q) && !c.preview.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sourceFilters: {
    value: 'all' | ConvSource;
    label: string;
  }[] = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'cli',
    label: 'CLI'
  }, {
    value: 'web',
    label: 'Web'
  }, {
    value: 'slack',
    label: 'Slack'
  }];
  return <div className="flex flex-col" data-id="element-9118" data-mp="2bvwifg0ssl2p">
      {/* Header row: title + Users/Assistants + Private/Shared */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-9119" data-mp="26xcrt2b9el40">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-9120" data-mp="2llps2bhdqq4b">Conversations</h1>
        <div className="flex items-center gap-3 flex-wrap" data-id="element-9121" data-mp="2lntt8lnw9b0w">
          <SegmentedToggle ariaLabel="Filter by owner" value={owner} onChange={setOwner} options={[{
          value: 'user',
          label: 'Users',
          icon: <UserCircle size={15} data-id="element-9123" data-mp="1nyh2o5e9cwx1" />
        }, {
          value: 'assistant',
          label: 'Assistants',
          icon: <Sparkles size={15} data-id="element-9124" data-mp="22w1452by49yg" />
        }]} data-id="element-9122" data-mp="2go7gsefw3yt5" />
          <div className="hidden sm:block w-px h-6 bg-neutral-200" data-id="element-9125" data-mp="22wszcc1ms5o7" />
          <SegmentedToggle ariaLabel="Filter by visibility" value={scope} onChange={setScope} options={[{
          value: 'private',
          label: 'Private',
          icon: <Lock size={14} data-id="element-9127" data-mp="39btuifiu0jtq" />
        }, {
          value: 'shared',
          label: 'Shared',
          icon: <Users size={14} data-id="element-9128" data-mp="3gvck54mkzbba" />
        }]} data-id="element-9126" data-mp="1xvdae8wc1f0j" />
        </div>
      </div>

      {/* Filter row: author avatars + source filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-9129" data-mp="3lv7an72g1yvt">
        <div className="flex items-center gap-2" data-id="element-9130" data-mp="37vg41re9bkru">
          <button onClick={() => setAuthorId(null)} aria-label="All authors" aria-pressed={authorId === null} className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${authorId === null ? 'bg-neutral-200 text-neutral-700' : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'}`} data-id="element-9131" data-mp="37wr87sbm6ujh">
            <LayoutGrid size={16} data-id="element-9132" data-mp="3qtikhhsqo9e1" />
          </button>
          <div className="flex items-center gap-2" data-id="element-9133" data-mp="3qsqz0zsuvs1h">
            {CONV_AUTHORS.map(a => {
            const selected = authorId === a.id;
            return <button key={a.id} onClick={() => setAuthorId(selected ? null : a.id)} title={a.name} aria-label={a.name} aria-pressed={selected} className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 transition-all ${a.color} ${selected ? 'ring-2 ring-offset-2 ring-[#EA580C]' : authorId ? 'opacity-40 hover:opacity-100' : 'hover:opacity-90'}`} data-id="element-9134" data-mp="mfanv89d0cgo">
                  {a.initials}
                </button>;
          })}
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-9135" data-mp="3apabdck0qqr3">
          <div className="relative hidden md:block" data-id="element-9136" data-mp="35tv26xwyhj10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} data-id="element-9137" data-mp="2i2z4xl9qdvmd" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search conversations..." className="pl-9 pr-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-sm focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors w-56" data-id="element-9138" data-mp="2d3w5qhgfmjmb" />
          </div>
          <div role="tablist" aria-label="Filter by source" className="inline-flex items-center gap-1" data-id="element-9139" data-mp="1zc7ushhx616x">
            {sourceFilters.map(s => {
            const selected = source === s.value;
            return <button key={s.value} role="tab" aria-selected={selected} onClick={() => setSource(s.value)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selected ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}`} data-id="element-9140" data-mp="9vm1se64q0n0">
                  {s.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-neutral-200 rounded-xl overflow-hidden" data-id="element-9141" data-mp="sllr925vthqy">
        {filtered.length === 0 ? <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-9142" data-mp="xl6mp2luf7yx">
            <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-3" data-id="element-9143" data-mp="jlylk464dps3">
              <MessageSquare size={18} className="text-neutral-400" data-id="element-9144" data-mp="oip36vxkotfm" />
            </div>
            <p className="text-sm font-semibold text-neutral-700" data-id="element-9145" data-mp="34albget29j7i">
              No conversations match these filters
            </p>
            <p className="text-xs text-neutral-500 mt-1" data-id="element-9146" data-mp="398eitb63oj9d">
              Try a different owner, source, or clear the author selection.
            </p>
          </div> : <div className="divide-y divide-neutral-100" data-id="element-9147" data-mp="19w7geomwwg5o">
            {filtered.map((c, i) => {
          const author = CONV_AUTHORS.find(a => a.id === c.authorId);
          return <div key={i} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer" data-id="element-9148" data-mp="11343chg6i5bk" data-mpi={i}>
                  <div className="flex items-center gap-4 min-w-0" data-id="element-9149" data-mp="w5cdru30avaj">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${author?.color ?? 'bg-neutral-100 text-neutral-500'}`} data-id="element-9150" data-mp="r4ex5lxna9b5">
                      {author?.initials ?? '?'}
                    </div>
                    <div className="flex flex-col min-w-0" data-id="element-9151" data-mp="i630p3a3khas">
                      <span className="font-semibold text-neutral-900 text-sm truncate" data-id="element-9152" data-mp="2gil6a6l5ocrr">
                        {c.title}
                      </span>
                      <span className="text-xs text-neutral-500 truncate" data-id="element-9153" data-mp="2cg6j5aagequo">
                        {c.preview}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0" data-id="element-9154" data-mp="378nw32b4l07">
                    <span className="hidden sm:inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 capitalize" data-id="element-9155" data-mp="81cvmojecx2c">
                      {c.source}
                    </span>
                    <span className="text-xs font-medium text-neutral-400" data-id="element-9156" data-mp="3biv2asnfny16">
                      {c.time}
                    </span>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
}
function EmptyStateOverview({
  onStartTask,
  onNavigate
}: {
  onStartTask: () => void;
  onNavigate: (view: string) => void;
}) {
  // DEBUG: mirror the connection + activity debug toggles so the populated vs
  // empty states can be previewed. `hasTasks` follows the activity toggle —
  // when Giga has been doing work, it also has things awaiting your review.
  const [toolsConnected, setToolsConnected] = useState(false);
  const [hasActivity, setHasActivity] = useState(false);
  const hasTasks = hasActivity;
  // Tasks: the handful of things Giga paused on and needs the user's call.
  const tasks = [{
    title: 'Approve 3 overdue-invoice reminders',
    detail: 'Giga drafted follow-ups to clients and is holding for your OK.',
    tool: 'gmail',
    cta: 'Review',
    nav: 'conversations',
    accent: 'orange' as const
  }, {
    title: 'Confirm the supplier reorder',
    detail: '5 products are running low — approve the restock PO for Acme.',
    tool: 'shopify',
    cta: 'Confirm',
    nav: 'activity',
    accent: 'orange' as const
  }, {
    title: 'Reconnect Slack to resume the #leadership recap',
    detail: 'A permissions error paused one pulse. Reconnect to continue.',
    tool: 'slack',
    cta: 'Reconnect',
    nav: 'integrations',
    accent: 'rose' as const
  }];
  const recentActivity = [{
    task: 'Sent overdue-invoice reminders to 3 clients',
    tool: 'gmail',
    type: 'Chat',
    status: 'done',
    time: '4m ago'
  }, {
    task: 'Enriching 6 new leads with firmographics',
    tool: 'hubspot',
    type: 'Journey',
    status: 'running',
    time: 'now'
  }, {
    task: 'Posted the weekly digest to #leadership',
    tool: 'slack',
    type: 'Pulse',
    status: 'done',
    time: '1h ago'
  }];
  return <div className="flex flex-col h-full" data-id="element-9157" data-mp="20t57e6fw5qyz">
      <div className="flex items-center justify-between mb-8" data-id="element-9158" data-mp="25lg7b9amtcrj">
        <h2 className="text-2xl font-bold text-neutral-900" data-id="element-9159" data-mp="25mrab6w96ghl">Overview</h2>
        <div className="flex items-center gap-2" data-id="element-9160" data-mp="1qxlbvl58ouar">
          {/* DEBUG toggles — preview populated vs empty states */}
          <button onClick={() => setToolsConnected(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${toolsConnected ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-9161" data-mp="1vw6chloelfzq">
            {toolsConnected ? 'tools: connected' : 'tools: none'}
          </button>
          <button onClick={() => setHasActivity(v => !v)} className={`px-2.5 py-1 rounded-full border border-dashed text-[11px] font-mono font-semibold uppercase tracking-wide transition-colors ${hasActivity ? 'border-[#EA580C]/40 bg-[#EA580C]/[0.06] text-[#C2410C]' : 'border-neutral-300 bg-white text-neutral-500'}`} data-id="element-9162" data-mp="3js33cpxqomob">
            {hasActivity ? 'activity: on' : 'activity: off'}
          </button>
          <button className="px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-9163" data-mp="6fathurkj8a4">
            Customize
          </button>
        </div>
      </div>

      {/* ── Core Areas ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6 mb-8" data-id="element-9164" data-mp="2n12bk3qf5vcx">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-9165" data-mp="2i3sjegbnb5le">
          {/* Conversations */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-9166" data-mp="xmsxe4p3bp1u">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-9167" data-mp="12lw1nmx8xr8r">
              <div className="flex items-center gap-2" data-id="element-9168" data-mp="16lwh9aw308he">
                <MessageSquare size={16} className="text-neutral-500" data-id="element-9169" data-mp="1bd64y5y2l1qn" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9170" data-mp="1bfirkwerw0a1">
                  Conversations
                </h3>
              </div>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-9171" data-mp="1laarw7zqp18i">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-9172" data-mp="1qbqew5lh9htj">
                No conversations yet.
              </span>
            </div>
          </div>

          {/* Journeys */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-9173" data-mp="3s2trh0exh6gq">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-9174" data-mp="3w4exc1t1okhf">
              <div className="flex items-center gap-2" data-id="element-9175" data-mp="3d63n8vfzexcr">
                <Map size={16} className="text-neutral-500" data-id="element-9176" data-mp="388jxozdu2y5i" />
                <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9177" data-mp="3881xjro1vzsb">
                  Journeys
                </h3>
              </div>
              <button className="text-xs font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors" data-id="element-9178" data-mp="3hyd7pvybv57n">
                Pinned Journeys <ChevronDown size={12} data-id="element-9179" data-mp="2u8srvlxh0o2l" />
              </button>
            </div>
            <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-9180" data-mp="2par6uvghieoh">
              <span className="text-sm text-neutral-400 font-medium" data-id="element-9181" data-mp="33a0cj172dqe7">
                No pinned journeys.
              </span>
            </div>
          </div>
        </div>

        {/* Assistants */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col" data-id="element-9182" data-mp="ihoqwqrant4c">
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100" data-id="element-9183" data-mp="djdfiblcpak0">
            <div className="flex items-center gap-2" data-id="element-9184" data-mp="lzcjwoo24veg">
              <UserCircle size={16} className="text-neutral-500" data-id="element-9185" data-mp="q30qp54v7myn" />
              <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9186" data-mp="q065hbe11y46">
                Assistants
              </h3>
            </div>
          </div>
          <div className="p-8 flex-1 flex flex-col items-center justify-center text-center" data-id="element-9187" data-mp="h2vt1mph42xo">
            <span className="text-sm text-neutral-400 font-medium" data-id="element-9188" data-mp="14r5aydjkg6jj">
              No assistants configured yet.
            </span>
          </div>
        </div>
      </div>

      {/* ── Tasks ─────────────────────────────────────────────────────────
                      Work Giga teed up that needs your call. Populates with the activity
                      toggle; otherwise shows a calm "your tasks live here" empty state. */}
      <div className="mb-8" data-id="element-9189" data-mp="1nooj7ey79l4w">
        <div className="flex items-center justify-between mb-4" data-id="element-9190" data-mp="1nogbe1gxp60k">
          <div className="flex items-center gap-2.5" data-id="element-9191" data-mp="1sikg0e2q91yh">
            <h3 className="text-base font-semibold text-neutral-900" data-id="element-9192" data-mp="2uhieegdtthp4">Tasks</h3>
            {hasTasks && <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EA580C] text-white text-xs font-bold" data-id="element-9193" data-mp="2zftqyyvia6b1">
                {tasks.length}
              </span>}
          </div>
          {hasTasks && <span className="text-xs font-medium text-neutral-400" data-id="element-9194" data-mp="394lbwn25fqcg">
              Needs your review
            </span>}
        </div>
        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-9195" data-mp="2bk8vd0cey68e">
          {!hasTasks ? <div className="flex items-center gap-4 px-5 py-8" data-id="element-9196" data-mp="2gj28grnsfv81">
              <span className="w-11 h-11 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center shrink-0" data-id="element-9197" data-mp="2ler0m9xha9ub">
                <ListChecks size={18} className="text-neutral-400" data-id="element-9198" data-mp="q3c18md3h3x" />
              </span>
              <div data-id="element-9199" data-mp="sfox7d9j1a4">
                <p className="text-sm font-semibold text-neutral-900" data-id="element-9200" data-mp="4retgqv30w11">
                  Your tasks live here
                </p>
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-9201" data-mp="1p8nyu786flhb">
                  Whenever Giga needs a quick approval or decision, it&rsquo;ll
                  land here for you to review.
                </p>
              </div>
            </div> : <div className="divide-y divide-neutral-100" data-id="element-9202" data-mp="1gf2iwkk9zy6b">
              {tasks.map((t, i) => <div key={i} className="px-5 py-4 flex items-start gap-4 hover:bg-neutral-50 transition-colors" data-id="element-9203" data-mp="1bi2eee32r3hb" data-mpi={i}>
                  <span className={`relative w-10 h-10 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-sm ${t.accent === 'rose' ? 'border-rose-200' : 'border-neutral-200'}`} data-id="element-9204" data-mp="16h4yy99g8nji">
                    <img src={`https://cdn.simpleicons.org/${t.tool}/475569`} className="w-5 h-5 object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-9205" data-mp="xke2hvqzj6oe" />
                    <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ring-2 ring-white ${t.accent === 'rose' ? 'bg-rose-500' : 'bg-[#EA580C]'}`} data-id="element-9206" data-mp="2my95eo98fl0l" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-9207" data-mp="3audund5ktcho">
                    <p className="text-sm font-semibold text-neutral-900 leading-snug" data-id="element-9208" data-mp="2x1o9tc1sadm5">
                      {t.title}
                    </p>
                    <p className="text-sm text-neutral-500 leading-relaxed mt-0.5" data-id="element-9209" data-mp="321air9akx7p5">
                      {t.detail}
                    </p>
                  </div>
                  <button onClick={() => onNavigate(t.nav)} className={`shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${t.accent === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-[#2B211A] hover:bg-[#1A1410] text-white'}`} data-id="element-9210" data-mp="3ts6x6ecafkej">
                    {t.cta}
                    <ArrowRight size={14} data-id="element-9211" data-mp="24b5vfevm0ptf" />
                  </button>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Activity + Connection containers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1" data-id="element-9212" data-mp="21r9j0z7oveko">
        {/* Recent activity */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-9213" data-mp="1ws6edfvmfad8">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-9214" data-mp="2arxzs1ogvt19">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9215" data-mp="25uz2ja9ib5yu">
              Recent activity
            </h3>
            {hasActivity && <button onClick={() => onNavigate('activity')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-9216" data-mp="1i28vnfggncvf">
                View all
              </button>}
          </div>
          {hasActivity ? <div className="divide-y divide-neutral-100" data-id="element-9217" data-mp="1n3ohh9qgpnev">
              {recentActivity.map((item, i) => <div key={i} className="px-5 py-3.5 flex items-start gap-3.5" data-id="element-9218" data-mp="zdu66wlsk6wq" data-mpi={i}>
                  <span className="relative w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-9219" data-mp="zbr43e7d6nbn">
                    <img src={`https://cdn.simpleicons.org/${item.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://cdn.simpleicons.org/box/475569';
              }} data-id="element-9220" data-mp="1829ajelbxvnf" />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white ${item.status === 'running' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-9221" data-mp="1mqvz86wujzrb" />
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-9222" data-mp="1vq99h1ssosd3">
                    <div className="flex items-start justify-between gap-3" data-id="element-9223" data-mp="1vt55ulozv8zf">
                      <p className="text-sm font-medium text-neutral-900 leading-snug" data-id="element-9224" data-mp="20qolnq1cbtth">
                        {item.task}
                      </p>
                      <span className="text-xs text-neutral-400 shrink-0" data-id="element-9225" data-mp="6cdwthkqwznp">
                        {item.time}
                      </span>
                    </div>
                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100 text-[10px] font-semibold uppercase tracking-wide text-neutral-500" data-id="element-9226" data-mp="bdbbvlagldkz">
                      {item.type}
                    </span>
                  </div>
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-9227" data-mp="2ftopepcz4wsa">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-9228" data-mp="2avdfkemoima2">
                <Activity size={20} className="text-neutral-400" data-id="element-9229" data-mp="37i04bc1ux5n3" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-9230" data-mp="33gmxozqxwyf4">
                No recent activity
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-9231" data-mp="2tktnvo4lvj11">
                When Giga completes tasks, runs automations, or answers your
                questions, they will appear here.
              </p>
              <button onClick={onStartTask} className="px-4 py-2 bg-[#2B211A] hover:bg-[#1A1410] text-white rounded-lg text-sm font-medium transition-colors" data-id="element-9232" data-mp="1i552h1702s2c">
                Start a new chat
              </button>
            </div>}
        </div>

        {/* Connection state */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden" data-id="element-9233" data-mp="1am5kmzz6t8l3">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100" data-id="element-9234" data-mp="1fkq9k38zo5u8">
            <h3 className="text-sm font-semibold text-neutral-900" data-id="element-9235" data-mp="10wc9g02yivhd">
              Connected tools
            </h3>
            <button onClick={() => onNavigate('integrations')} className="text-xs font-medium text-neutral-500 hover:text-neutral-900 transition-colors" data-id="element-9236" data-mp="15n2e1b3k6ub0">
              Manage
            </button>
          </div>
          {toolsConnected ? <div className="divide-y divide-neutral-100" data-id="element-9237" data-mp="vxiue380uwf3">
              {[{
            tool: 'slack',
            label: 'Slack',
            ok: true
          }, {
            tool: 'gmail',
            label: 'Gmail',
            ok: true
          }, {
            tool: 'notion',
            label: 'Notion',
            ok: false
          }].map((c, i) => <div key={i} className="px-5 py-3.5 flex items-center gap-3.5" data-id="element-9238" data-mp="3uh2f8em7kwmd" data-mpi={i}>
                  <span className="w-9 h-9 rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-9239" data-mp="3uj6shn5zz7zb">
                    <img src={`https://cdn.simpleicons.org/${c.tool}/475569`} className="w-4.5 h-[18px] object-contain" alt="" data-id="element-9240" data-mp="88ko9mlmha0l" />
                  </span>
                  <span className="flex-1 text-sm font-medium text-neutral-900" data-id="element-9241" data-mp="m70vdened2j9">
                    {c.label}
                  </span>
                  {c.ok ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full pl-2 pr-2.5 py-1" data-id="element-9242" data-mp="vy5807plobax">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-9243" data-mp="r2g6vzxxmmci" />
                      Connected
                    </span> : <button onClick={() => onNavigate('integrations')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full pl-2 pr-2.5 py-1 transition-colors" data-id="element-9244" data-mp="10yt9jnbggaje">
                      <AlertTriangle size={11} data-id="element-9245" data-mp="2cb0w1jxczdy4" />
                      Reconnect
                    </button>}
                </div>)}
            </div> : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-9246" data-mp="2h9awa8xxx4ql">
              <div className="w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-9247" data-mp="2lc7e3xfnowth">
                <Plug size={20} className="text-neutral-400" data-id="element-9248" data-mp="1tlksenqdn5dh" />
              </div>
              <h4 className="text-base font-semibold text-neutral-900 mb-2" data-id="element-9249" data-mp="1xnfu3tc7zkrs">
                Connect your tools
              </h4>
              <p className="text-sm text-neutral-500 max-w-sm mb-6" data-id="element-9250" data-mp="22eebkw8y2owm">
                Giga works best when it can access your apps. Connect Slack,
                Gmail, Notion, and more to get started.
              </p>
              <button onClick={() => onNavigate('integrations')} className="px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 rounded-lg text-sm font-medium transition-colors" data-id="element-9251" data-mp="19wisb9e523u9">
                View integrations
              </button>
            </div>}
        </div>
      </div>

      {/* ── Your Impact ─────────────────────────────────────────────────────────
          The high-level metrics moved down here so the top of the overview can
          focus on actionable areas (Conversations, Journeys, Assistants). */}
      <div className="mt-8 pt-8 border-t border-neutral-200" data-id="element-9252" data-mp="eoqdd8dyw6gt">
        <h3 className="text-base font-semibold text-neutral-900 mb-4" data-id="element-9253" data-mp="en6rn29jl37n">
          Your Impact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-9254" data-mp="9pwvl3rndtau">
          {[{
          label: 'Tasks Completed',
          value: hasActivity ? '128' : '0',
          note: hasActivity ? '+12% this week' : 'Ready for your first task',
          icon: CheckCircle2
        }, {
          label: 'Hours Saved',
          value: hasActivity ? '46' : '0',
          note: hasActivity ? '~6 days of work' : 'Let Giga do the heavy lifting',
          icon: Clock
        }, {
          label: 'Active Automations',
          value: hasActivity ? '4' : '0',
          note: hasActivity ? 'Running smoothly' : 'Create a pulse or journey',
          icon: Zap
        }].map((stat, i) => <div key={i} className={`bg-white rounded-xl border border-neutral-200 p-5 shadow-sm flex flex-col gap-3 transition-opacity ${hasActivity ? '' : 'opacity-60'}`} data-id="element-9255" data-mp="3boml3px9a2lk" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-9256" data-mp="32wswdg7kplkw">
                <span className="text-sm font-medium text-neutral-500" data-id="element-9257" data-mp="32vhvbo5iirxh">
                  {stat.label}
                </span>
                <stat.icon size={16} className="text-neutral-400" data-id="element-9258" data-mp="2szeqgaja0f6u" />
              </div>
              <div className="flex items-baseline gap-2" data-id="element-9259" data-mp="2o025jvbyti6w">
                <span className="text-3xl font-bold text-neutral-900" data-id="element-9260" data-mp="2nzk50mi9sht6">
                  {stat.value}
                </span>
              </div>
              <span className="text-xs font-medium text-neutral-400" data-id="element-9261" data-mp="zgpnjn0gti3r">
                {stat.note}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
// Giga-specific onboarding checklist. Each step maps to a real part of getting
// Giga working across your apps — not a generic marketing funnel. The dashboard
// section a step links to is used to jump the user straight there.
type OnboardingStep = {
  id: number;
  title: string;
  desc: string;
  cta: string;
  nav: string;
  done?: boolean;
};
const ONBOARDING_STEPS: OnboardingStep[] = [{
  id: 1,
  title: 'Create your Giga account',
  desc: 'Your workspace is ready — Giga is standing by.',
  cta: 'Done',
  nav: 'overview',
  done: true
}, {
  id: 2,
  title: 'Connect your tools',
  desc: 'Link Gmail, Notion, and the rest of your stack so Giga can act on your behalf across the tools you already use.',
  cta: 'Connect other tools',
  nav: 'integrations'
}, {
  id: 3,
  title: '@mention Giga in a chat',
  desc: 'Add Giga to any Slack channel and just @mention it to kick off your first task.',
  cta: 'Open conversations',
  nav: 'conversations'
}, {
  id: 4,
  title: 'Set up a Pulse',
  desc: 'Let Giga watch for something and act automatically — like a morning briefing or a reply nudge.',
  cta: 'Create a pulse',
  nav: 'pulses'
}, {
  id: 5,
  title: 'Build your first Journey',
  desc: 'Chain a few steps into a repeatable workflow Giga can run end to end.',
  cta: 'Create a journey',
  nav: 'journeys'
}];
function OnboardingWidget({
  onNavigate
}: {
  onNavigate: (nav: string) => void;
}) {
  const [expanded, setExpanded] = useState<number | null>(2);
  const [minimized, setMinimized] = useState(false);
  // Slack is required to create a Giga account, so by the time the user reaches
  // this dashboard it is always connected. Detected here (true) so the
  // "Connect your tools" step can show Slack as linked instead of asking again.
  const slackConnected = true;
  const completed = ONBOARDING_STEPS.filter(s => s.done).length;
  const pct = Math.round(completed / ONBOARDING_STEPS.length * 100);
  if (minimized) {
    return <button onClick={() => setMinimized(false)} aria-label="Open setup checklist" className="fixed bottom-6 left-6 md:left-[256px] z-50 flex items-center gap-2.5 h-11 pl-2.5 pr-4 rounded-full bg-[#2B211A] hover:bg-[#1A1410] text-white shadow-xl transition-colors" data-id="element-9262" data-mp="2editexgya5cs">
        <span className="w-6 h-6 rounded-full bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-9263" data-mp="2t1m2ifqahx5t">
          <Sparkles size={13} className="text-white" data-id="element-9264" data-mp="2x2pgsxsovl4x" />
        </span>
        <span className="text-sm font-semibold" data-id="element-9265" data-mp="2x51towjlb5b4">Set up Giga · {pct}%</span>
      </button>;
  }
  return <div className="fixed bottom-6 left-6 md:left-[256px] z-50 w-80 bg-white rounded-2xl shadow-xl border border-[#E7E0D6] overflow-hidden" data-id="element-9266" data-mp="2j9ga54w4ldq3">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 bg-[#F5EFE6] border-b border-[#E7E0D6]" data-id="element-9267" data-mp="3bz6p1g5l0jn9">
        <div className="flex items-start justify-between mb-1" data-id="element-9268" data-mp="3grg9whruatio">
          <div className="flex items-center gap-2" data-id="element-9269" data-mp="3gsriqw056rgn">
            <span className="w-6 h-6 rounded-md bg-[#2B211A] flex items-center justify-center shrink-0" data-id="element-9270" data-mp="1s66q214e41lm">
              <Sparkles size={13} className="text-[#EA580C]" data-id="element-9271" data-mp="1n5991rv49dlp" />
            </span>
            <h3 className="text-base font-bold text-[#2B211A]" data-id="element-9272" data-mp="1j3np5mk5zz20">
              Get Giga working
            </h3>
          </div>
          <button onClick={() => setMinimized(true)} aria-label="Minimize" className="text-neutral-400 hover:text-[#2B211A] transition-colors" data-id="element-9273" data-mp="1e6x7wvwmixf0">
            <Minus size={16} data-id="element-9274" data-mp="26u9v98alz99d" />
          </button>
        </div>
        <div className="flex items-center justify-between mb-3 pl-8" data-id="element-9275" data-mp="26utbmkkm1pla">
          <span className="text-sm text-neutral-500" data-id="element-9276" data-mp="2229pv0brro5e">
            {completed} of {ONBOARDING_STEPS.length} done
          </span>
          <span className="text-sm font-semibold text-[#EA580C]" data-id="element-9277" data-mp="1x3yg0plh5dn6">{pct}%</span>
        </div>
        {/* Progress bar */}
        <div className="h-2 w-full bg-[#E7DCCB] rounded-full overflow-hidden ml-8 max-w-[calc(100%-2rem)]" data-id="element-9278" data-mp="2ulf6fa5ltddc">
          <motion.div className="h-full bg-[#EA580C] rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${pct}%`
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-9279" data-mp="2x40kw6mrgb2w" />
        </div>
      </div>

      {/* Accordion items */}
      <div className="flex flex-col px-2 py-2 max-h-[46vh] overflow-y-auto" data-id="element-9280" data-mp="374kkl4umt7j0">
        {ONBOARDING_STEPS.map(step => {
        const isOpen = expanded === step.id;
        return <div key={step.id} className="rounded-xl" data-id="element-9281" data-mp="3g2mjneut1q3n">
              <button onClick={() => setExpanded(isOpen ? null : step.id)} className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F5EFE6] transition-colors text-left" data-id="element-9282" data-mp="3l1fvz3ycvb28">
                <div className="flex items-center gap-3 min-w-0" data-id="element-9283" data-mp="3us29msmihria">
                  {step.done ? <span className="w-5 h-5 rounded-full bg-[#2B211A] flex items-center justify-center text-white shrink-0" data-id="element-9284" data-mp="2o9svwv8sra2">
                      <Check size={12} strokeWidth={3} data-id="element-9285" data-mp="7o4izy771cu2" />
                    </span> : <span className="w-5 h-5 rounded-full border-2 border-dashed border-[#C9BBA6] shrink-0" data-id="element-9286" data-mp="23q7nab00jg60" />}
                  <span className={`text-sm font-medium truncate ${step.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-9287" data-mp="1zow0ed8ix91i">
                    {step.title}
                  </span>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-neutral-400 shrink-0" data-id="element-9288" data-mp="28o9exkbi8nt3" /> : <ChevronDown size={16} className="text-neutral-400 shrink-0" data-id="element-9289" data-mp="2ne6vbe37097q" />}
              </button>
              <AnimatePresence initial={false} data-id="element-9290" data-mp="2ifdtwk16k8nx">
                {isOpen && !step.done && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-9291" data-mp="2ih82qzz6gabn">
                    <div className="pl-11 pr-3 pb-3 pt-0.5" data-id="element-9292" data-mp="x3c3i32kxhf6">
                      <p className="text-sm text-neutral-500 leading-relaxed mb-3" data-id="element-9293" data-mp="38omepxcltnxx">
                        {step.desc}
                      </p>
                      {/* Slack is connected during account creation, so on the
                       "Connect your tools" step we surface it as already-linked
                       (detected) rather than asking for it again, and offer a
                       separate button to connect the rest of the stack. */}
                      {step.id === 2 && slackConnected && <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[#E7E0D6] bg-[#F5EFE6] px-3 py-2" data-id="element-9294" data-mp="2uy96dtvsw782">
                          <span className="flex items-center gap-2 min-w-0" data-id="element-9295" data-mp="2gzrjfydpyyr7">
                            <img src="https://cdn.simpleicons.org/slack" alt="" aria-hidden="true" className="w-4 h-4 shrink-0 object-contain" data-id="element-9296" data-mp="2l0bgz327g4dv" />
                            <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-9297" data-mp="f4qvj43xnmuo">
                              Slack
                            </span>
                          </span>
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-id="element-9298" data-mp="33625z1pvc5me">
                            <Check size={13} strokeWidth={3} data-id="element-9299" data-mp="2te4vxyaljjp9" />
                            Connected
                          </span>
                        </div>}
                      <button onClick={() => onNavigate(step.nav)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2B211A] hover:bg-[#1A1410] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-9300" data-mp="2of1pc9eyx5f6">
                        {step.cta}
                        <ArrowRight size={14} data-id="element-9301" data-mp="2aeqwj0u3lgrd" />
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}